/**
 * usePropertyDetailsForm Hook
 * 
 * Manages all form-related state, handlers, and computed values for the
 * Global Property Details Form, including state actions, transition changes,
 * change types, and form submission logic.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Form, message } from 'antd';
import { GlobalPropertiesService } from '../services/api/globalPropertiesService';
import {
  PropertyDefinition,
  StateActions,
  StateAction,
  StateActionEntry,
  TransitionChange,
  TransitionChangeReference,
  ChangeTypeDefinition,
  CalculationDependencies,
  ValidationRules,
  getChangeTypeName,
  normalizeStateAction
} from '../types/globalProperties';
import { StateActionConfig } from '../components/GlobalProperties/StateActionDialog';
import { getForwardTransitions, getBackwardTransitions, StandardTransitionInfo } from '../types/standardStates';
import { apiClient } from '../services/apiClient';
import {
  convertStateActionsToArray,
  convertStateActionsToNested,
  parseReferenceBasedTransitionChanges,
  extractNamedConfigurations,
  buildReferenceBasedTransitionChanges,
  ExtractedConfigurations,
  getAvailableNamedConfigurations
} from '../utils/globalPropertyUtils';
import { isReferenceBasedTransitionChange } from '../types/globalProperties';

interface UsePropertyDetailsFormProps {
  selectedPropertyKey: string | null;
  showEditForm: boolean;
  onPropertySelect?: (propertyKey: string) => void;
  onFormSuccess?: () => void;
  onCloseForm?: () => void;
}

export const usePropertyDetailsForm = ({
  selectedPropertyKey,
  showEditForm,
  onPropertySelect,
  onFormSuccess,
  onCloseForm
}: UsePropertyDetailsFormProps) => {
  const [detailsForm] = Form.useForm();
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [optionCatalogs, setOptionCatalogs] = useState<Array<{ id: string; name: string }>>([]);
  const [detailsPropertyType, setDetailsPropertyType] = useState<string>('string');
  const [stateActions, setStateActions] = useState<StateActions | undefined>(undefined);
  const [localStateActions, setLocalStateActions] = useState<StateActions | undefined>(undefined);
  const [transitionChanges, setTransitionChanges] = useState<TransitionChange[]>([]);
  const [localTransitionChanges, setLocalTransitionChanges] = useState<TransitionChange[]>([]);
  const [editingTransitionChange, setEditingTransitionChange] = useState<TransitionChange | undefined>(undefined);
  const [showTransitionChangeEditor, setShowTransitionChangeEditor] = useState(false);
  const [changeTypeDefinitions, setChangeTypeDefinitions] = useState<ChangeTypeDefinition[]>([]);
  const [localChangeTypeDefinitions, setLocalChangeTypeDefinitions] = useState<TransitionChangeReference[]>([]);
  const [selectedChangeType, setSelectedChangeType] = useState<TransitionChangeReference | null>(null);
  const [calculationDependencies, setCalculationDependencies] = useState<CalculationDependencies | Record<string, any> | undefined>(undefined);
  const [validationRules, setValidationRules] = useState<ValidationRules | Record<string, any> | undefined>(undefined);
  const [transitionFilter, setTransitionFilter] = useState<'all' | 'state_action'>('all');
  const [stateActionsModified, setStateActionsModified] = useState<boolean>(false);
  const [stateActionDialog, setStateActionDialog] = useState<{
    visible: boolean;
    state: string;
    flowType: 'operational' | 'improvement';
    currentStateAction?: StateAction;
    currentFlowDirection?: 'forward' | 'backward' | 'both';
    currentChangeTypeRef?: string;
    forwardAction?: StateAction;
    forwardChangeTypeRef?: string;
    backwardAction?: StateAction;
    backwardChangeTypeRef?: string;
  } | null>(null);

  // Load option catalogs for details form
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const response = await apiClient.get('/option-catalogs');
        if (Array.isArray(response.data)) {
          setOptionCatalogs(response.data.map((cat: any) => ({
            id: cat.id || cat.name,
            name: cat.name || cat.id
          })));
        }
      } catch (error) {
        console.warn('Failed to load option catalogs:', error);
        setOptionCatalogs([]);
      }
    };
    loadCatalogs();
  }, []);

  // Reset form when property changes
  useEffect(() => {
    if (!selectedPropertyKey) {
      detailsForm.resetFields();
      setStateActions(undefined);
      setLocalStateActions(undefined);
      setStateActionsModified(false);
    }
  }, [selectedPropertyKey, detailsForm]);

  // Load property definition when selected for editing
  const loadPropertyDefinitionForDetails = useCallback(async () => {
    if (!selectedPropertyKey) return;
    
    // Reset modification flag at the start of loading - we're loading fresh data
    // This ensures we always load from backend on a fresh property load
    setStateActionsModified(false);
    
    setDetailsLoading(true);
    try {
      const response = await GlobalPropertiesService.getPropertyDefinition(selectedPropertyKey);
      const def = response.definition;
      
      // Log the full backend response for state_actions
      console.log('[usePropertyDetailsForm] ========== BACKEND RESPONSE - LOADING PROPERTY ==========');
      console.log('[usePropertyDetailsForm] Property Key:', selectedPropertyKey);
      console.log('[usePropertyDetailsForm] Full Response:', JSON.stringify(response, null, 2));
      console.log('[usePropertyDetailsForm] State Actions from Backend:', {
        raw: def.state_actions,
        isArray: Array.isArray(def.state_actions),
        isObject: typeof def.state_actions === 'object' && def.state_actions !== null,
        type: typeof def.state_actions,
        value: def.state_actions,
        stringified: JSON.stringify(def.state_actions, null, 2),
        count: Array.isArray(def.state_actions) ? def.state_actions.length : 'not array'
      });
      console.log('[usePropertyDetailsForm] Transition Changes from Backend:', {
        raw: def.transition_changes,
        isArray: Array.isArray(def.transition_changes),
        count: Array.isArray(def.transition_changes) ? def.transition_changes.length : 'not array',
        stringified: JSON.stringify(def.transition_changes, null, 2)
      });
      console.log('[usePropertyDetailsForm] ========================================================');
      
      setDetailsPropertyType(def.property_type);
      
      detailsForm.setFieldsValue({
        property_key: def.property_key,
        property_type: def.property_type,
        description: def.description,
        input_source: def.input_source || 'user_input',
        allow_user_input: def.input_source === 'user_input',
        // For list-type properties: prefer reference from calculation_dependencies, fallback to global_option_catalog_ref
        global_option_catalog_ref: def.property_type === 'list' && def.calculation_dependencies?.reference
          ? def.calculation_dependencies.reference
          : def.global_option_catalog_ref,
        // Validation rules
        validation_required: def.validation_rules?.required,
        validation_min: def.validation_rules?.min,
        validation_max: def.validation_rules?.max,
        validation_min_length: def.validation_rules?.min_length,
        validation_max_length: def.validation_rules?.max_length,
        validation_pattern: def.validation_rules?.pattern,
        // List-type validation rules
        validation_limit_to_list: def.validation_rules?.limit_to_list,
        validation_update_scope: def.validation_rules?.update_scope,
        // Calculation dependencies
        calculation_type: def.calculation_dependencies?.type,
        source_properties: def.calculation_dependencies?.source_properties
          ? Object.entries(def.calculation_dependencies.source_properties).map(([operator, prop]) => ({
              operator: operator,
              property_key: prop.property_key,
              source: prop.source,
              description: prop.description
            }))
          : [],
        // For ALL property types with user input: load user_input_label and help_text from calculation_dependencies
        // Try user_input1.label first (for all property types), then fallback to list-type top-level fields
        user_input_label: def.calculation_dependencies?.user_input1?.label ||
          (def.property_type === 'list' 
            ? (def.calculation_dependencies?.user_input_label || def.calculation_dependencies?.user_input?.label || def.user_input_parameters?.label)
            : undefined),
        user_input_help_text: def.calculation_dependencies?.user_input1?.help_text ||
          (def.property_type === 'list'
            ? (def.calculation_dependencies?.user_input_help_text || def.calculation_dependencies?.user_input?.help_text || def.user_input_parameters?.help_text)
            : undefined),
        user_input_default_value: def.property_type === 'list'
          ? (def.calculation_dependencies?.user_input_default_value !== undefined 
              ? def.calculation_dependencies.user_input_default_value 
              : (def.calculation_dependencies?.user_input?.default_value !== undefined 
                  ? def.calculation_dependencies.user_input.default_value 
                  : undefined))
          : undefined,
        allow_multiple_selections: def.property_type === 'list'
          ? (def.calculation_dependencies?.allow_multiple_selections || def.calculation_dependencies?.user_input?.allow_multiple_selections || false)
          : undefined,
        // For string-type properties: load input mask from calculation_dependencies
        user_input_mask: def.property_type === 'string'
          ? (def.calculation_dependencies?.user_input_mask || def.calculation_dependencies?.user_input?.input_mask)
          : undefined,
        user_input_mask_pattern: def.property_type === 'string'
          ? (def.calculation_dependencies?.user_input_mask_pattern || def.calculation_dependencies?.user_input?.input_mask_pattern)
          : undefined,
        // For list-type properties: reference can be in calculation_dependencies or global_option_catalog_ref
        reference: def.property_type === 'list'
          ? (def.calculation_dependencies?.reference || def.calculation_dependencies?.reference_id || def.global_option_catalog_ref)
          : undefined,
        // State actions (array format)
        state_actions: def.state_actions || [],
        // Referenced properties (for References tab) - load from calculation_dependencies or top-level field
        referenced_properties: def.calculation_dependencies?.referenced_properties || def.referenced_properties || [],
        // Load validation_rules including named configurations (resource1, condition1, validation1, etc.)
        validation_rules: def.validation_rules || {}
      });
      
      // Set state actions - now using array format directly
      let loadedStateActions: StateActionEntry[] | undefined = undefined;
      if (def.state_actions) {
        if (Array.isArray(def.state_actions)) {
          loadedStateActions = def.state_actions;
        } else {
          // Backward compatibility: convert nested format to array
          loadedStateActions = convertStateActionsToArray(def.state_actions, localChangeTypeDefinitions);
        }
      }
      
      // Convert to nested format for internal state management (needed for StateActionDialog)
      let stateActionsNested: StateActions | undefined = undefined;
      
      if (loadedStateActions && loadedStateActions.length > 0) {
        stateActionsNested = convertStateActionsToNested(loadedStateActions);
        
        // Double-check that the nested format isn't empty
        if (stateActionsNested && typeof stateActionsNested === 'object') {
          const hasOperational = stateActionsNested.operational && Object.keys(stateActionsNested.operational).length > 0;
          const hasImprovement = stateActionsNested.improvement && Object.keys(stateActionsNested.improvement).length > 0;
          if (!hasOperational && !hasImprovement) {
            stateActionsNested = undefined;
          }
        }
      }
      
      // Always load state_actions from backend - the flag was reset at the start of load
      // This ensures we always get fresh data from the backend when loading
      setStateActions(stateActionsNested);
      setLocalStateActions(stateActionsNested);
      
      // Load transition_changes
      let loadedTransitionChanges: (TransitionChange | TransitionChangeReference)[] = [];
      if (def.transition_changes) {
        if (typeof def.transition_changes === 'string') {
          try {
            loadedTransitionChanges = JSON.parse(def.transition_changes);
          } catch (e) {
            console.warn('Failed to parse transition_changes as JSON:', e);
            loadedTransitionChanges = [];
          }
        } else if (Array.isArray(def.transition_changes)) {
          loadedTransitionChanges = def.transition_changes;
        }
      }
      setTransitionChanges(loadedTransitionChanges as TransitionChange[]);
      setLocalTransitionChanges(loadedTransitionChanges as TransitionChange[]);
      
      // Store transition_changes directly in reference-based format
      let loadedTransitionChangeReferences: TransitionChangeReference[] = [];
      
      if (loadedTransitionChanges && loadedTransitionChanges.length > 0) {
        // Filter for reference-based transition changes only
        loadedTransitionChangeReferences = loadedTransitionChanges.filter(
          (tc): tc is TransitionChangeReference => isReferenceBasedTransitionChange(tc)
        ) as TransitionChangeReference[];
      }
      
      // For backward compatibility: if we have change_type_definitions but no transition_changes,
      // convert them to TransitionChangeReference format
      if (loadedTransitionChangeReferences.length === 0 && def.change_type_definitions) {
        let loadedChangeTypeDefinitions: ChangeTypeDefinition[] = [];
        if (typeof def.change_type_definitions === 'string') {
          try {
            loadedChangeTypeDefinitions = JSON.parse(def.change_type_definitions);
          } catch (e) {
            console.warn('Failed to parse change_type_definitions as JSON:', e);
          }
        } else if (Array.isArray(def.change_type_definitions)) {
          loadedChangeTypeDefinitions = def.change_type_definitions;
        }
        
        // Convert to reference-based format if we have change type definitions
        if (loadedChangeTypeDefinitions.length > 0) {
          const extractedConfigs = extractNamedConfigurations(loadedChangeTypeDefinitions);
          loadedTransitionChangeReferences = buildReferenceBasedTransitionChanges(
            loadedChangeTypeDefinitions,
            extractedConfigs.referenceMapping
          );
        }
      }
      
      setChangeTypeDefinitions([]); // Keep for backward compatibility but not used for new format
      // Always load transition_changes from backend - the flag was reset at the start of load
      // This ensures we always get fresh data from the backend when loading
      setLocalChangeTypeDefinitions(loadedTransitionChangeReferences);
      
      // Store calculation_dependencies and validation_rules for named configuration extraction
      setCalculationDependencies(def.calculation_dependencies);
      setValidationRules(def.validation_rules);
      
      // Also set these in form to ensure they're available for the named config components
      detailsForm.setFieldsValue({
        calculation_dependencies: def.calculation_dependencies || {},
        validation_rules: def.validation_rules || {}
      });
    } catch (error: any) {
      console.error('Error loading property definition:', error);
      void message.error('Failed to load property definition');
    } finally {
      setDetailsLoading(false);
    }
  }, [selectedPropertyKey, detailsForm]);

  // Auto-load property definition when selected
  useEffect(() => {
    if (selectedPropertyKey && showEditForm) {
      setLocalStateActions(undefined);
      loadPropertyDefinitionForDetails();
    }
  }, [selectedPropertyKey, showEditForm, loadPropertyDefinitionForDetails]);

  // Helper to auto-generate required named configurations for a change type
  // If a configuration of the required type already exists, use that instead of creating a new one
  const autoGenerateRequiredConfigurations = useCallback((
    stateActionType: 'System' | 'User' | 'Calculate' | 'Reference',
    changeTypeName: string
  ): { configName: string; configObject: any; configType: 'calculation_dependencies' | 'validation_rules'; isExisting: boolean } | null => {
    const baseConfigName = stateActionType === 'User' ? 'user_input' : 
                           stateActionType === 'Reference' ? 'reference' : 
                           'calculation';
    
    // Check if a configuration of this type already exists (check both state and form values)
    // Merge state and form to get all existing configurations
    const formCalcDeps = detailsForm.getFieldValue('calculation_dependencies') || {};
    const mergedCalcDeps = {
      ...(calculationDependencies && typeof calculationDependencies === 'object' ? calculationDependencies : {}),
      ...(formCalcDeps && typeof formCalcDeps === 'object' ? formCalcDeps : {})
    };
    
    const existingConfigs = stateActionType === 'User' || stateActionType === 'Reference' || stateActionType === 'System' || stateActionType === 'Calculate'
      ? Object.keys(mergedCalcDeps).filter(k => k.match(new RegExp(`^${baseConfigName}\\d+$`)))
      : [];
    
    // If an existing configuration exists, use the first one (typically user_input1, calculation1, reference1)
    if (existingConfigs.length > 0) {
      // Sort to get the lowest number first (e.g., user_input1 before user_input2)
      existingConfigs.sort((a, b) => {
        const numA = parseInt(a.match(/\d+$/)?.[0] || '0', 10);
        const numB = parseInt(b.match(/\d+$/)?.[0] || '0', 10);
        return numA - numB;
      });
      
      const existingConfigName = existingConfigs[0];
      const existingConfigObject = mergedCalcDeps[existingConfigName];
      
      console.log(`[autoGenerateRequiredConfigurations] Using existing ${baseConfigName} configuration: ${existingConfigName}`, {
        stateActionType,
        changeTypeName,
        existingConfigName,
        existingConfigObject,
        foundInState: !!calculationDependencies?.[existingConfigName],
        foundInForm: !!formCalcDeps[existingConfigName]
      });
      
      return {
        configName: existingConfigName,
        configObject: existingConfigObject,
        configType: 'calculation_dependencies',
        isExisting: true
      };
    }
    
    // No existing configuration found - create a new one
    const existingNumbers = existingConfigs.map(k => parseInt(k.match(/\d+$/)?.[0] || '0', 10));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const configName = `${baseConfigName}${nextNumber}`;
    
    // Create default configuration object based on type
    let configObject: any = {};
    
    if (stateActionType === 'User') {
      configObject = {
        label: `${changeTypeName} User Input`,
        help_text: '',
        default_value: undefined
      };
    } else if (stateActionType === 'System' || stateActionType === 'Calculate') {
      configObject = {
        type: 'function',
        calculation_type: 'function',
        function_name: '',
        function_parameters: {}
      };
    } else if (stateActionType === 'Reference') {
      configObject = {
        artifact_type: undefined,
        artifact_item: 'operational',
        property_key: undefined,
        filter: undefined,
        selection_method: 'process',
        source: 'same_artifact',
        reference_type: 'simple',
        description: undefined
      };
    }
    
    console.log(`[autoGenerateRequiredConfigurations] Creating new ${baseConfigName} configuration: ${configName}`, {
      stateActionType,
      changeTypeName,
      configName,
      configObject
    });
    
    return {
      configName,
      configObject,
      configType: 'calculation_dependencies',
      isExisting: false
    };
  }, [calculationDependencies, detailsForm]);

  // Helper to auto-create change types from state actions
  const autoCreateChangeTypesFromStateActions = useCallback((stateActions: StateActions | undefined): TransitionChangeReference[] => {
    if (!stateActions) return [];
    
    const changeTypes: TransitionChangeReference[] = [];
    const actionTypeCounts: Record<string, number> = {};
    const generatedConfigs: Array<{ name: string; config: any }> = [];
    
    const getChangeTypeNameFromAction = (actionType: string): string => {
      const normalized = normalizeStateAction(actionType as StateAction);
      if (normalized === 'none') return '';
      
      const baseName = normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
      
      if (!actionTypeCounts[baseName]) {
        actionTypeCounts[baseName] = 0;
      }
      actionTypeCounts[baseName]++;
      
      if (actionTypeCounts[baseName] > 1) {
        return `${baseName}${actionTypeCounts[baseName]}`;
      }
      
      return baseName;
    };
    
    const seenActions = new Set<string>();
    const actionToConfig: Record<string, { flow: 'operational' | 'improvement', state: string }[]> = {};
    
    if (stateActions.operational) {
      Object.entries(stateActions.operational).forEach(([state, action]) => {
        const normalized = normalizeStateAction(action);
        if (normalized !== 'none') {
          const key = normalized;
          if (!actionToConfig[key]) {
            actionToConfig[key] = [];
          }
          actionToConfig[key].push({ flow: 'operational', state });
          seenActions.add(key);
        }
      });
    }
    
    if (stateActions.improvement) {
      Object.entries(stateActions.improvement).forEach(([state, action]) => {
        const normalized = normalizeStateAction(action);
        if (normalized !== 'none') {
          const key = normalized;
          if (!actionToConfig[key]) {
            actionToConfig[key] = [];
          }
          actionToConfig[key].push({ flow: 'improvement', state });
          seenActions.add(key);
        }
      });
    }
    
    Object.entries(actionToConfig).forEach(([actionType, configs]) => {
      const changeTypeName = getChangeTypeNameFromAction(actionType);
      if (!changeTypeName) return;
      
      const existing = localChangeTypeDefinitions.find(ct => ct.change_type_name === changeTypeName);
      
      if (!existing) {
        const normalized = normalizeStateAction(actionType as StateAction);
        const stateActionType = normalized as 'System' | 'User' | 'Calculate' | 'Reference';
        
        const newChangeType: TransitionChangeReference = {
          change_type_name: changeTypeName,
          state_action_type: stateActionType,
        };
        
        // Auto-generate or find existing required configuration
        const generatedConfig = autoGenerateRequiredConfigurations(stateActionType, changeTypeName);
        if (generatedConfig) {
          // Only add to generatedConfigs if it's a new configuration (not existing)
          if (!generatedConfig.isExisting) {
            generatedConfigs.push({
              name: generatedConfig.configName,
              config: generatedConfig.configObject
            });
          }
          
          // Reference the configuration in the change type (whether existing or new)
          if (stateActionType === 'User') {
            newChangeType.user_input = generatedConfig.configName;
          } else if (stateActionType === 'System' || stateActionType === 'Calculate') {
            newChangeType.calculation = generatedConfig.configName;
          } else if (stateActionType === 'Reference') {
            newChangeType.reference = generatedConfig.configName;
          }
        }
        
        changeTypes.push(newChangeType);
      }
    });
    
    // Add all generated configurations to calculationDependencies at once (both state and form)
    if (generatedConfigs.length > 0) {
      setCalculationDependencies(prev => {
        const updated = { ...(prev || {}) };
        generatedConfigs.forEach(({ name, config }) => {
          updated[name] = config;
        });
        
        // Also update form
        const currentFormCalcDeps = detailsForm.getFieldValue('calculation_dependencies') || {};
        const updatedFormCalcDeps = { ...currentFormCalcDeps };
        generatedConfigs.forEach(({ name, config }) => {
          updatedFormCalcDeps[name] = config;
        });
        detailsForm.setFieldsValue({
          calculation_dependencies: updatedFormCalcDeps
        });
        
        return updated;
      });
    }
    
    return changeTypes;
  }, [localChangeTypeDefinitions, autoGenerateRequiredConfigurations, detailsForm]);

  // Helper to build state action details from change types
  const buildStateActionDetails = useCallback(() => {
    const details: Record<string, Record<string, { forward?: StateAction; backward?: StateAction; both?: StateAction }>> = {
      operational: {},
      improvement: {}
    };

    const inferActionFromChangeType = (ct: TransitionChangeReference): StateAction | null => {
      return ct.state_action_type || null;
    };

    const findChangeTypesForState = (
      stateAction: StateAction | undefined,
      _flowType: 'operational' | 'improvement',
      _state: string
    ) => {
      if (!stateAction) return { forward: null, backward: null, both: null };

      const normalizedAction = normalizeStateAction(stateAction);
      
      // Note: TransitionChangeReference doesn't have flow_direction - it's derived from state_actions
      // For now, match by state_action_type
      const forwardChangeType = localChangeTypeDefinitions.find(ct => {
        const inferredAction = inferActionFromChangeType(ct);
        if (inferredAction === normalizedAction) {
          return ct.change_type_name.toLowerCase().startsWith(normalizedAction.toLowerCase());
        }
        return false;
      });

      let backwardChangeType = localChangeTypeDefinitions.find(ct => {
        const inferredAction = inferActionFromChangeType(ct);
        return ct.change_type_name.toLowerCase().startsWith(normalizedAction.toLowerCase()) ||
               inferredAction === normalizedAction;
      });

      if (!backwardChangeType && forwardChangeType) {
        // Try to find a different one
        backwardChangeType = localChangeTypeDefinitions.find(ct => 
          ct.change_type_name !== forwardChangeType.change_type_name &&
          inferActionFromChangeType(ct) === normalizedAction
        ) || localChangeTypeDefinitions.find(ct => 
          ct.change_type_name !== forwardChangeType.change_type_name
        );
      }

      const bothChangeType = localChangeTypeDefinitions.find(ct => {
        const inferredAction = inferActionFromChangeType(ct);
        return (inferredAction === normalizedAction) || 
               ct.change_type_name.toLowerCase().startsWith(normalizedAction.toLowerCase());
      });

      return { forward: forwardChangeType || undefined, backward: backwardChangeType || undefined, both: bothChangeType || undefined };
    };

    ['operational', 'improvement'].forEach(flowType => {
      const states = flowType === 'operational' 
        ? ['Proposed', 'Active', 'Complete', 'Cancelled']
        : ['Target', 'Current', 'Completed'];

      states.forEach(state => {
        const stateAction = localStateActions?.[flowType as 'operational' | 'improvement']?.[state];
        
        const { forward: forwardChangeType, backward: backwardChangeType, both: bothChangeType } = 
          findChangeTypesForState(stateAction, flowType as 'operational' | 'improvement', state);

        if (bothChangeType) {
          const inferredAction = inferActionFromChangeType(bothChangeType) || stateAction || 'none';
          details[flowType][state] = { both: inferredAction as StateAction };
        } else if (forwardChangeType && backwardChangeType) {
          const forwardActionType = inferActionFromChangeType(forwardChangeType) || stateAction || 'none';
          const backwardActionType = inferActionFromChangeType(backwardChangeType) || stateAction || 'none';
          
          details[flowType][state] = { 
            forward: forwardActionType as StateAction,
            backward: backwardActionType as StateAction
          };
        } else if (forwardChangeType) {
          const forwardActionType = inferActionFromChangeType(forwardChangeType) || stateAction || 'none';
          details[flowType][state] = { forward: forwardActionType as StateAction };
        } else if (backwardChangeType) {
          const backwardActionType = inferActionFromChangeType(backwardChangeType) || stateAction || 'none';
          details[flowType][state] = { backward: backwardActionType as StateAction };
        } else if (stateAction) {
          details[flowType][state] = { both: stateAction };
        }
      });
    });

    return details;
  }, [localStateActions, localChangeTypeDefinitions]);

  // Helper to detect operational → improvement reference patterns
  const improvementPatterns = useMemo(() => {
    const stateActionsArray = convertStateActionsToArray(localStateActions);
    const patterns: Record<string, { operational: StateActionEntry; improvement?: StateActionEntry }> = {};
    
    const operationalReferences = stateActionsArray.filter(
      entry => entry.flow_type === 'operational' && entry.state_action_type === 'Reference'
    );
    
    operationalReferences.forEach(opRef => {
      const improvementAction = stateActionsArray.find(
        entry => entry.flow_type === 'improvement' && 
                 entry.state === opRef.state && 
                 entry.state_action_type !== 'none'
      );
      
      if (improvementAction) {
        patterns[opRef.state] = {
          operational: opRef,
          improvement: improvementAction
        };
      }
    });
    
    return patterns;
  }, [localStateActions]);

  // Handler to delete a change type
  const handleDeleteChangeType = useCallback((changeType: TransitionChangeReference, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    const changeTypeName = changeType.change_type_name;
    
    const stateActionsArray = convertStateActionsToArray(localStateActions);
    const references = stateActionsArray.filter(
      entry => entry.change_type === changeTypeName
    );
    
    if (references.length > 0) {
      const stateList = references.map(r => `${r.flow_type}.${r.state}`).join(', ');
      void message.warning(
        `Cannot delete change type "${changeTypeName}". It is used by: ${stateList}. Please remove it from those states first.`,
        5
      );
      return;
    }
    
    setLocalChangeTypeDefinitions(prev => 
      prev.filter(ct => ct.change_type_name !== changeTypeName)
    );
    
    if (selectedChangeType && selectedChangeType.change_type_name === changeTypeName) {
      setSelectedChangeType(null);
    }
    
    // Mark state actions as modified when change types are deleted
    setStateActionsModified(true);
    
    void message.success(`Change type "${changeTypeName}" deleted successfully`);
  }, [localStateActions, selectedChangeType]);

  // Helper to create change type from state action with auto-generated configurations
  const createChangeTypeFromStateAction = useCallback((action: StateAction, _flowDirection: 'forward' | 'backward' | 'both'): TransitionChangeReference => {
    const normalized = normalizeStateAction(action);
    const baseName = normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
    
    const stateActionType = normalized as 'System' | 'User' | 'Calculate' | 'Reference';
    
    const newChangeType: TransitionChangeReference = {
      change_type_name: baseName,
      state_action_type: stateActionType,
    };
    
    // Auto-generate or find existing required configuration
    const generatedConfig = autoGenerateRequiredConfigurations(stateActionType, baseName);
    if (generatedConfig) {
      // Only add to state and form if it's a new configuration (not existing)
      if (!generatedConfig.isExisting) {
        // Add the configuration to calculationDependencies (both state and form)
        setCalculationDependencies(prev => {
          const updated = { ...(prev || {}) };
          updated[generatedConfig.configName] = generatedConfig.configObject;
          
          // Also update form
          const currentFormCalcDeps = detailsForm.getFieldValue('calculation_dependencies') || {};
          detailsForm.setFieldsValue({
            calculation_dependencies: {
              ...currentFormCalcDeps,
              [generatedConfig.configName]: generatedConfig.configObject
            }
          });
          
          return updated;
        });
      }
      
      // Reference the configuration in the change type (whether existing or new)
      if (stateActionType === 'User') {
        newChangeType.user_input = generatedConfig.configName;
      } else if (stateActionType === 'System' || stateActionType === 'Calculate') {
        newChangeType.calculation = generatedConfig.configName;
      } else if (stateActionType === 'Reference') {
        newChangeType.reference = generatedConfig.configName;
      }
    }
    
    return newChangeType;
  }, [autoGenerateRequiredConfigurations, detailsForm]);

  // Helper to get state action config from change types
  const getStateActionConfigFromChangeTypes = useCallback((state: string, flowType: 'operational' | 'improvement') => {
    const currentAction = localStateActions?.[flowType]?.[state];
    
    if (!currentAction) {
      return {
        currentStateAction: undefined,
        currentFlowDirection: 'both' as const
      };
    }

    const normalizedAction = normalizeStateAction(currentAction);
    
    const forwardChangeType = localChangeTypeDefinitions.find(ct => {
      return ct.change_type_name.toLowerCase().startsWith(normalizedAction.toLowerCase()) &&
             ct.state_action_type === normalizedAction;
    });

    const backwardChangeType = localChangeTypeDefinitions.find(ct => {
      return ct.change_type_name.toLowerCase().startsWith(normalizedAction.toLowerCase()) &&
             ct.state_action_type === normalizedAction &&
             ct !== forwardChangeType;
    });

    const bothChangeType = localChangeTypeDefinitions.find(ct => {
      return ct.change_type_name.toLowerCase().startsWith(normalizedAction.toLowerCase()) &&
             ct.state_action_type === normalizedAction;
    });

    if (bothChangeType) {
      return {
        currentStateAction: currentAction,
        currentFlowDirection: 'both' as const,
        currentChangeTypeRef: bothChangeType.change_type_name
      };
    } else if (forwardChangeType && backwardChangeType) {
      const forwardActionType: StateAction = forwardChangeType.state_action_type || currentAction;
      const backwardActionType: StateAction = backwardChangeType.state_action_type || currentAction;
      
      return {
        currentStateAction: forwardActionType,
        currentFlowDirection: 'forward' as const,
        forwardAction: forwardActionType,
        forwardChangeTypeRef: forwardChangeType.change_type_name,
        backwardAction: backwardActionType,
        backwardChangeTypeRef: backwardChangeType.change_type_name
      };
    } else if (forwardChangeType) {
      return {
        currentStateAction: currentAction,
        currentFlowDirection: 'forward' as const,
        forwardAction: currentAction,
        forwardChangeTypeRef: forwardChangeType.change_type_name
      };
    } else if (backwardChangeType) {
      return {
        currentStateAction: currentAction,
        currentFlowDirection: 'backward' as const,
        backwardAction: currentAction,
        backwardChangeTypeRef: backwardChangeType.change_type_name
      };
    }

    return {
      currentStateAction: currentAction,
      currentFlowDirection: 'both' as const
    };
  }, [localStateActions, localChangeTypeDefinitions]);

  // Handler for opening state action dialog
  const handleStateClick = useCallback((state: string, flowType: 'operational' | 'improvement') => {
    const config = getStateActionConfigFromChangeTypes(state, flowType);
    setStateActionDialog({
      visible: true,
      state,
      flowType,
      currentStateAction: config.currentStateAction,
      currentFlowDirection: config.currentFlowDirection,
      currentChangeTypeRef: config.currentChangeTypeRef,
      forwardAction: config.forwardAction,
      forwardChangeTypeRef: config.forwardChangeTypeRef,
      backwardAction: config.backwardAction,
      backwardChangeTypeRef: config.backwardChangeTypeRef
    });
  }, [getStateActionConfigFromChangeTypes]);

  // Helper function to auto-generate required configuration for a change type and update it
  const ensureRequiredConfiguration = useCallback((
    changeType: TransitionChangeReference
  ): TransitionChangeReference => {
    const stateActionType = changeType.state_action_type;
    
    // Check if required reference already exists
    if (stateActionType === 'User' && changeType.user_input) {
      return changeType;
    }
    if ((stateActionType === 'System' || stateActionType === 'Calculate') && changeType.calculation) {
      return changeType;
    }
    if (stateActionType === 'Reference' && changeType.reference) {
      return changeType;
    }
    
    // Auto-generate or find existing required configuration
    const generatedConfig = autoGenerateRequiredConfigurations(stateActionType, changeType.change_type_name);
    if (generatedConfig) {
      // Only update state and form if it's a new configuration (not existing)
      if (!generatedConfig.isExisting) {
        // Update both state and form
        setCalculationDependencies(prev => {
          const updated = { ...(prev || {}) };
          updated[generatedConfig.configName] = generatedConfig.configObject;
          
          // Also update form
          const currentFormCalcDeps = detailsForm.getFieldValue('calculation_dependencies') || {};
          detailsForm.setFieldsValue({
            calculation_dependencies: {
              ...currentFormCalcDeps,
              [generatedConfig.configName]: generatedConfig.configObject
            }
          });
          
          return updated;
        });
      }
      
      // Return updated change type with reference (whether existing or new)
      const updatedChangeType = { ...changeType };
      if (stateActionType === 'User') {
        updatedChangeType.user_input = generatedConfig.configName;
      } else if (stateActionType === 'System' || stateActionType === 'Calculate') {
        updatedChangeType.calculation = generatedConfig.configName;
      } else if (stateActionType === 'Reference') {
        updatedChangeType.reference = generatedConfig.configName;
      }
      return updatedChangeType;
    }
    
    return changeType;
  }, [autoGenerateRequiredConfigurations, detailsForm]);

  // Handler for saving state action from dialog
  const handleStateActionDialogSave = useCallback((config: StateActionConfig) => {
    const { state, flowType, flowDirection, forward, backward, both } = config;
    
    const currentStateActionsArray = convertStateActionsToArray(localStateActions);
    
    const filteredEntries = currentStateActionsArray.filter(
      entry => !(entry.state === state && entry.flow_type === flowType)
    );
    
    const newEntries: StateActionEntry[] = [];
    const newChangeTypes: TransitionChangeReference[] = [];
    const updatedChangeTypes: TransitionChangeReference[] = [];
    const messages: string[] = [];

    const processChangeType = (
      stateAction: StateAction,
      changeTypeRef: string | undefined,
      direction: 'forward' | 'backward' | 'both'
    ): string => {
      let changeTypeName = changeTypeRef?.trim() || '';
      
      if (!changeTypeName) {
        // No change type specified - create a new one
        const newChangeType = createChangeTypeFromStateAction(stateAction, direction);
        const existingNames = [...localChangeTypeDefinitions.map(ct => ct.change_type_name), ...newChangeTypes.map(ct => ct.change_type_name)];
        let baseName = newChangeType.change_type_name;
        let counter = 1;
        changeTypeName = baseName;
        while (existingNames.includes(changeTypeName)) {
          counter++;
          changeTypeName = `${baseName}${counter}`;
        }
        newChangeType.change_type_name = changeTypeName;
        newChangeTypes.push(newChangeType);
        messages.push(`Auto-created change type: ${changeTypeName}`);
      } else {
        // Change type name provided - check if it exists
        const existingChangeType = localChangeTypeDefinitions.find(
          ct => ct.change_type_name === changeTypeName
        );
        if (!existingChangeType) {
          // Change type doesn't exist - create it
          const newChangeType = createChangeTypeFromStateAction(stateAction, direction);
          newChangeType.change_type_name = changeTypeName;
          newChangeTypes.push(newChangeType);
          messages.push(`Created change type: ${changeTypeName}`);
        } else {
          // Change type exists - update its state_action_type if it has changed
          const normalizedStateAction = normalizeStateAction(stateAction);
          const newStateActionType = normalizedStateAction as 'System' | 'User' | 'Calculate' | 'Reference';
          
          if (existingChangeType.state_action_type !== newStateActionType) {
            // State action type has changed - update the change type
            const updated = { 
              ...existingChangeType,
              state_action_type: newStateActionType
            };
            
            // If the state_action_type changed, we may need to update the configuration reference
            // Remove old reference and ensure new one exists
            if (existingChangeType.state_action_type === 'User' && newStateActionType !== 'User') {
              delete updated.user_input;
            }
            if ((existingChangeType.state_action_type === 'System' || existingChangeType.state_action_type === 'Calculate') && 
                newStateActionType !== 'System' && newStateActionType !== 'Calculate') {
              delete updated.calculation;
            }
            if (existingChangeType.state_action_type === 'Reference' && newStateActionType !== 'Reference') {
              delete updated.reference;
            }
            
            // Ensure required configuration exists for new state_action_type
            const ensuredChangeType = ensureRequiredConfiguration(updated);
            updatedChangeTypes.push(ensuredChangeType);
            messages.push(`Updated change type ${changeTypeName} from ${existingChangeType.state_action_type} to ${newStateActionType}`);
          } else {
            // State action type hasn't changed - just keep the existing change type
            const updated = { ...existingChangeType };
            updatedChangeTypes.push(updated);
          }
        }
      }
      
      return changeTypeName;
    };

    if (flowDirection === 'both') {
      if (both === null) {
        // Remove state action
      } else if (both) {
        const changeTypeName = processChangeType(both.state_action, both.change_type_ref, 'both');
        newEntries.push({
          state,
          flow_type: flowType,
          state_action_type: both.state_action,
          flow_direction: 'both',
          change_type: changeTypeName
        });
      }
    } else if (flowDirection === 'forward') {
      if (forward === null) {
        // Remove forward state action
      } else if (forward) {
        const forwardChangeTypeName = processChangeType(forward.state_action, forward.change_type_ref, 'forward');
        newEntries.push({
          state,
          flow_type: flowType,
          state_action_type: forward.state_action,
          flow_direction: 'forward',
          change_type: forwardChangeTypeName
        });
      }
      
      if (forward !== null && backward && backward.state_action) {
        const backwardChangeTypeName = processChangeType(backward.state_action, backward.change_type_ref, 'backward');
        newEntries.push({
          state,
          flow_type: flowType,
          state_action_type: backward.state_action,
          flow_direction: 'backward',
          change_type: backwardChangeTypeName
        });
      }
    } else if (flowDirection === 'backward') {
      if (backward === null) {
        // Remove backward state action
      } else if (backward) {
        const backwardChangeTypeName = processChangeType(backward.state_action, backward.change_type_ref, 'backward');
        newEntries.push({
          state,
          flow_type: flowType,
          state_action_type: backward.state_action,
          flow_direction: 'backward',
          change_type: backwardChangeTypeName
        });
      }
      
      if (backward !== null && forward && forward.state_action) {
        const forwardChangeTypeName = processChangeType(forward.state_action, forward.change_type_ref, 'forward');
        newEntries.push({
          state,
          flow_type: flowType,
          state_action_type: forward.state_action,
          flow_direction: 'forward',
          change_type: forwardChangeTypeName
        });
      }
    }

    if (newChangeTypes.length > 0 || updatedChangeTypes.length > 0) {
      let updatedDefinitions = [...localChangeTypeDefinitions];
      
      updatedDefinitions = [...updatedDefinitions, ...newChangeTypes];
      
      updatedChangeTypes.forEach(updated => {
        const index = updatedDefinitions.findIndex(ct => ct.change_type_name === updated.change_type_name);
        if (index >= 0) {
          updatedDefinitions[index] = updated;
        }
      });
      
      setLocalChangeTypeDefinitions(updatedDefinitions);
      
      messages.forEach(msg => void message.info(msg));
    }

    const allEntries = [...filteredEntries, ...newEntries];
    const nestedStateActions = convertStateActionsToNested(allEntries);
    setLocalStateActions(nestedStateActions);
    
    // Clean up orphaned change types - change types that are no longer referenced by any state actions
    const allStateActionEntries = convertStateActionsToArray(nestedStateActions);
    const referencedChangeTypeNames = new Set(
      allStateActionEntries
        .map(entry => entry.change_type)
        .filter((name): name is string => !!name)
    );
    
    // Get the updated change type definitions (including new ones)
    const currentChangeTypeDefinitions = newChangeTypes.length > 0 || updatedChangeTypes.length > 0
      ? (() => {
          let updated = [...localChangeTypeDefinitions];
          updated = [...updated, ...newChangeTypes];
          updatedChangeTypes.forEach(updatedCt => {
            const index = updated.findIndex(ct => ct.change_type_name === updatedCt.change_type_name);
            if (index >= 0) {
              updated[index] = updatedCt;
            }
          });
          return updated;
        })()
      : localChangeTypeDefinitions;
    
    const orphanedChangeTypes = currentChangeTypeDefinitions.filter(
      ct => !referencedChangeTypeNames.has(ct.change_type_name)
    );
    
    if (orphanedChangeTypes.length > 0) {
      // Remove orphaned change types
      const cleanedChangeTypes = currentChangeTypeDefinitions.filter(
        ct => referencedChangeTypeNames.has(ct.change_type_name)
      );
      setLocalChangeTypeDefinitions(cleanedChangeTypes);
      
      console.log('[usePropertyDetailsForm] Removed orphaned change types:', {
        removed: orphanedChangeTypes.map(ct => ct.change_type_name),
        remaining: cleanedChangeTypes.map(ct => ct.change_type_name)
      });
      
      if (orphanedChangeTypes.length > 0) {
        void message.info(`Removed ${orphanedChangeTypes.length} orphaned change type(s): ${orphanedChangeTypes.map(ct => ct.change_type_name).join(', ')}`);
      }
    }
    
    setStateActionDialog(null);
    
    // Mark state actions as modified since user explicitly edited them
    setStateActionsModified(true);
  }, [localStateActions, localChangeTypeDefinitions, createChangeTypeFromStateAction, ensureRequiredConfiguration]);

  // Placeholder functions - these will need to be implemented with the full logic from GlobalPropertyTableView
  // Due to the massive size of these functions, they are stubbed here and will need to be fully implemented
  const handleDetailsFormSubmit = useCallback(async (fieldsToSave?: string[]) => {
    // Note: saveAll will be determined inside after reading from form values
    console.log('[usePropertyDetailsForm] handleDetailsFormSubmit CALLED', { fieldsToSave });
    console.log('[usePropertyDetailsForm] Current localChangeTypeDefinitions state:', {
      value: localChangeTypeDefinitions,
      isUndefined: localChangeTypeDefinitions === undefined,
      isArray: Array.isArray(localChangeTypeDefinitions),
      count: localChangeTypeDefinitions?.length || 0,
      names: localChangeTypeDefinitions?.map(ct => ct.change_type_name) || []
    });
    try {
      const values = await detailsForm.validateFields();
      
      // Check if fields to save are specified in form values (from Select dropdown)
      // fieldsToSave parameter takes precedence, but we also check form values
      const formFieldsToSave = values._fieldsToSave;
      const fieldsToSaveToUse = fieldsToSave && fieldsToSave.length > 0 ? fieldsToSave : (formFieldsToSave && formFieldsToSave.length > 0 ? formFieldsToSave : undefined);
      const saveAll = !fieldsToSaveToUse || fieldsToSaveToUse.length === 0;
      
      // Remove the temporary field from values
      delete values._fieldsToSave;
      
      // IMPORTANT: validation_rules and calculation_dependencies are updated via setFieldsValue but not bound to Form.Item,
      // so we need to get them directly from the form to ensure we capture named configurations
      // Use getFieldsValue(true) to get ALL field values including those not bound to Form.Item
      const formValues = detailsForm.getFieldsValue(true);
      console.log('[usePropertyDetailsForm] Form values after validation:', {
        validationRulesInForm: !!formValues.validation_rules,
        validationRulesKeys: formValues.validation_rules ? Object.keys(formValues.validation_rules) : [],
        calculationDepsInForm: !!formValues.calculation_dependencies,
        calculationDepsKeys: formValues.calculation_dependencies ? Object.keys(formValues.calculation_dependencies) : [],
        validationRulesSample: formValues.validation_rules ? JSON.stringify(Object.keys(formValues.validation_rules).slice(0, 5)) : 'none'
      });
      
      // Always merge validation_rules from form values (it contains resource, condition, validation configurations)
      if (formValues.validation_rules && typeof formValues.validation_rules === 'object') {
        values.validation_rules = formValues.validation_rules;
        console.log('[usePropertyDetailsForm] Merged validation_rules from form values into values object:', {
          keys: Object.keys(formValues.validation_rules),
          resourceKeys: Object.keys(formValues.validation_rules).filter(k => k.match(/^resource\d+$/)),
          conditionKeys: Object.keys(formValues.validation_rules).filter(k => k.match(/^condition\d+$/)),
          validationKeys: Object.keys(formValues.validation_rules).filter(k => k.match(/^validation\d+$/))
        });
      } else {
        console.warn('[usePropertyDetailsForm] No validation_rules found in form values - named configurations may not be saved');
        // Initialize empty object if not present
        values.validation_rules = {};
      }
      
      // Always merge calculation_dependencies from form values (it contains user_input, calculation, reference configurations)
      if (formValues.calculation_dependencies && typeof formValues.calculation_dependencies === 'object') {
        values.calculation_dependencies = formValues.calculation_dependencies;
        console.log('[usePropertyDetailsForm] Merged calculation_dependencies from form values into values object:', {
          keys: Object.keys(formValues.calculation_dependencies),
          userInputKeys: Object.keys(formValues.calculation_dependencies).filter(k => k.match(/^user_input\d+$/)),
          calculationKeys: Object.keys(formValues.calculation_dependencies).filter(k => k.match(/^calculation\d+$/)),
          referenceKeys: Object.keys(formValues.calculation_dependencies).filter(k => k.match(/^reference\d+$/))
        });
      } else if (!values.calculation_dependencies) {
        // Initialize empty object if not present
        values.calculation_dependencies = {};
      }
      
      console.log('[usePropertyDetailsForm] Form validation passed, values:', {
        property_key: values.property_key,
        hasLocalChangeTypes: localChangeTypeDefinitions !== undefined,
        localChangeTypesCount: localChangeTypeDefinitions?.length || 0,
        hasValidationRules: !!values.validation_rules,
        validationRulesKeys: values.validation_rules ? Object.keys(values.validation_rules) : [],
        validationRulesDetails: values.validation_rules ? Object.keys(values.validation_rules).filter(k => k.match(/^resource\d+$/)).map(k => ({
          key: k,
          hasConfig: !!values.validation_rules[k],
          configKeys: values.validation_rules[k] ? Object.keys(values.validation_rules[k]) : []
        })) : []
      });
      
      // Validate Boolean property conditions - at least one condition (true or false) must be defined
      if (values.property_type === 'boolean' && values.calculation_dependencies?.boolean_conditions) {
        const booleanConditions = values.calculation_dependencies.boolean_conditions;
        const hasTrueCondition = booleanConditions.true_condition && (
          booleanConditions.true_condition.user_input ||
          booleanConditions.true_condition.state_condition ||
          (booleanConditions.true_condition.property_value && booleanConditions.true_condition.property_value.length > 0)
        );
        const hasFalseCondition = booleanConditions.false_condition && (
          booleanConditions.false_condition.user_input ||
          booleanConditions.false_condition.state_condition ||
          (booleanConditions.false_condition.property_value && booleanConditions.false_condition.property_value.length > 0)
        );
        
        if (!hasTrueCondition && !hasFalseCondition) {
          void message.error('At least one condition (True or False) must be defined for Boolean properties');
          return;
        }
        
        // Validate state_condition: if artifact_item is 'reference', reference_artifact_item must be provided
        const validateCondition = (condition: any, conditionType: string) => {
          if (condition?.state_condition?.artifact_item === 'reference' && !condition.state_condition.reference_artifact_item) {
            void message.error(`${conditionType} condition: Reference artifact_item must be specified when artifact_item is 'reference'`);
            return false;
          }
          
          // Validate property_value conditions
          if (condition?.property_value && Array.isArray(condition.property_value)) {
            for (let i = 0; i < condition.property_value.length; i++) {
              const pv = condition.property_value[i];
              if (pv.artifact_item === 'reference' && !pv.reference_artifact_item) {
                void message.error(`${conditionType} condition: Property value condition ${i + 1}: Reference artifact_item must be specified when artifact_item is 'reference'`);
                return false;
              }
              if (!pv.property_key || !pv.operator || pv.value === undefined || pv.value === null || pv.value === '') {
                void message.error(`${conditionType} condition: Property value condition ${i + 1}: Property key, operator, and value must be specified`);
                return false;
              }
            }
          }
          return true;
        };
        
        if (booleanConditions.true_condition && !validateCondition(booleanConditions.true_condition, 'True')) {
          return;
        }
        if (booleanConditions.false_condition && !validateCondition(booleanConditions.false_condition, 'False')) {
          return;
        }
      }
      
      // Validate operators based on calculation type
      if (values.calculation_type && values.source_properties?.length > 0) {
        const operators = values.source_properties.map((p: any) => p.operator).filter(Boolean);
        
        if (values.calculation_type === 'divide') {
          const hasNumerator = operators.includes('numerator');
          const hasDenominator = operators.includes('denominator');
          if (!hasNumerator || !hasDenominator) {
            void message.error('Divide calculation requires exactly one numerator and one denominator');
            return;
          }
        } else if (values.calculation_type === 'subtract') {
          const hasOperand1 = operators.includes('operand1');
          const hasOperand2 = operators.includes('operand2');
          if (!hasOperand1 || !hasOperand2) {
            void message.error('Subtract calculation requires exactly one operand1 (subtract from) and one operand2 (subtract)');
            return;
          }
        } else if (values.calculation_type === 'multiply' || values.calculation_type === 'add') {
          const hasOperand1 = operators.includes('operand1');
          const hasOperand2 = operators.includes('operand2');
          if (!hasOperand1 || !hasOperand2) {
            void message.error(`${values.calculation_type === 'multiply' ? 'Multiply' : 'Add'} calculation requires at least operand1 and operand2`);
            return;
          }
        }
      }
      
      setDetailsLoading(true);

      // Build update payload - PropertyUpdate model only includes these fields (property_key is in URL, not body)
      const allowedFields = [
        'property_type', 'description', 'default_value', 'input_source',
        'global_option_catalog_ref', 'validation_rules', 'calculation_dependencies',
        'state_actions', 'transition_changes', 'referenced_properties'
      ];
      
      // Helper to convert empty strings to null for optional string fields
      const normalizeValue = (value: any): any => {
        if (value === undefined) return undefined;
        if (typeof value === 'string' && value === '') return null;
        return value;
      };
      
      const rawPayload: any = {};
      
      // Build validation_rules if it has at least one rule - ensure it's an object (dict)
      // CRITICAL: Start with state (loaded from backend), then merge form values (which include newly created named configs)
      // This ensures named configurations from tabs (resource1, condition1, validation1) are included
      // Only build validation_rules if saving all fields or explicitly selected
      let validationRulesPayload: any = {};
      if (saveAll || fieldsToSaveToUse?.includes('validation_rules')) {
        validationRulesPayload = {
          ...(validationRules && typeof validationRules === 'object' ? validationRules : {}), // Start with state
          ...(values.validation_rules && typeof values.validation_rules === 'object' ? values.validation_rules : {}) // Override with form values
        };
        
        // Include basic validation rules (apply to all property types)
        if (values.validation_required !== undefined) validationRulesPayload.required = values.validation_required;
        if (values.validation_min !== undefined) validationRulesPayload.min = values.validation_min;
        if (values.validation_max !== undefined) validationRulesPayload.max = values.validation_max;
        if (values.validation_min_length !== undefined) validationRulesPayload.min_length = values.validation_min_length;
        if (values.validation_max_length !== undefined) validationRulesPayload.max_length = values.validation_max_length;
        if (values.validation_pattern) validationRulesPayload.pattern = values.validation_pattern;
        
        // Property-type-specific validation rules
        // List-type validation rules
        if (values.property_type === 'list') {
        if (values.validation_limit_to_list !== undefined) {
          validationRulesPayload.limit_to_list = values.validation_limit_to_list;
        }
        // Only include update_scope if limit_to_list is false
        if (values.validation_limit_to_list === false && values.validation_update_scope) {
          validationRulesPayload.update_scope = values.validation_update_scope;
        } else if (values.validation_limit_to_list === true) {
          // Remove update_scope if limit_to_list is true
          delete validationRulesPayload.update_scope;
        }
          console.log('[usePropertyDetailsForm] Added list-type validation rules:', {
            limit_to_list: validationRulesPayload.limit_to_list,
            update_scope: validationRulesPayload.update_scope,
            property_type: values.property_type
          });
        }
        
        // URL-type validation rules: automatically add URL pattern validation if not explicitly set
        if (values.property_type === 'url') {
          // Use standard URL regex pattern if pattern is not explicitly provided
          // This pattern validates HTTP, HTTPS, FTP, and other common URL schemes
          // Pattern: matches URLs starting with scheme:// (required) followed by valid domain/path
          const urlPattern = '^(https?|ftp):\\/\\/([-\\w\\.])+(:\\d+)?(\\/([\\w/_\\.]*)?)?(\\?([-\\w=&%]+))?(#([-\\w]*))?$';
          
          // Only set automatic pattern if no pattern exists (neither from form nor from existing validationRules)
          if (!validationRulesPayload.pattern) {
            validationRulesPayload.pattern = urlPattern;
            console.log('[usePropertyDetailsForm] Added URL-type validation pattern:', {
              pattern: urlPattern,
              property_type: values.property_type
            });
          } else {
            // Pattern already exists (either user-provided or from backend), log for debugging
            console.log('[usePropertyDetailsForm] URL property using existing validation pattern:', {
              pattern: validationRulesPayload.pattern,
              property_type: values.property_type
            });
          }
        }
      }
      
      // Note: Basic validation rules (required, min, max, etc.) are added above and can coexist with named configurations
      // Named configs (resource1, condition1, validation1) are used by change types,
      // while basic rules serve as defaults
      
      // Always set validation_rules if it has at least one field (including named configurations)
      // Only include if saving all fields or explicitly selected
      if ((saveAll || fieldsToSaveToUse?.includes('validation_rules')) && Object.keys(validationRulesPayload).length > 0) {
        rawPayload.validation_rules = validationRulesPayload;
        console.log('[usePropertyDetailsForm] Saving validation_rules to rawPayload:', {
          keys: Object.keys(validationRulesPayload),
          hasResources: Object.keys(validationRulesPayload).some(k => k.match(/^resource\d+$/)),
          resourceKeys: Object.keys(validationRulesPayload).filter(k => k.match(/^resource\d+$/)),
          conditionKeys: Object.keys(validationRulesPayload).filter(k => k.match(/^condition\d+$/)),
          validationKeys: Object.keys(validationRulesPayload).filter(k => k.match(/^validation\d+$/)),
          listTypeValidation: {
            limit_to_list: validationRulesPayload.limit_to_list,
            update_scope: validationRulesPayload.update_scope
          },
          resourceConfigs: Object.keys(validationRulesPayload)
            .filter(k => k.match(/^resource\d+$/))
            .reduce((acc, key) => {
              acc[key] = validationRulesPayload[key];
              return acc;
            }, {} as Record<string, any>)
        });
      } else {
        console.log('[usePropertyDetailsForm] No validation_rules to save (empty object)');
      }
      
      // Build calculation_dependencies if it has a value - ensure it's an object (dict)
      // CRITICAL: Start with state (loaded from backend), then merge form values (which include newly created named configs)
      // This ensures named configurations from tabs (user_input1, calculation1, reference1) are included
      let calculationDeps: any = {
        ...(calculationDependencies && typeof calculationDependencies === 'object' ? calculationDependencies : {}), // Start with state
        ...(values.calculation_dependencies && typeof values.calculation_dependencies === 'object' ? values.calculation_dependencies : {}) // Override with form values
      };
      
      // For list-type properties: store user_input_label, help_text, default_value, allow_multiple_selections, and reference in calculation_dependencies
      // CRITICAL: Also convert these to a named user_input1 configuration for change types to reference
      if (values.property_type === 'list') {
        const hasUserInputFields = values.user_input_label || values.user_input_help_text || 
          (values.user_input_default_value !== undefined && values.user_input_default_value !== null && values.user_input_default_value !== '') ||
          values.allow_multiple_selections !== undefined;
        
        if (hasUserInputFields) {
          // Store as top-level fields (for backward compatibility)
          if (values.user_input_label) {
            calculationDeps.user_input_label = values.user_input_label;
          }
          if (values.user_input_help_text) {
            calculationDeps.user_input_help_text = values.user_input_help_text;
          }
          if (values.user_input_default_value !== undefined && values.user_input_default_value !== null && values.user_input_default_value !== '') {
            calculationDeps.user_input_default_value = values.user_input_default_value;
          }
          if (values.allow_multiple_selections !== undefined) {
            calculationDeps.allow_multiple_selections = values.allow_multiple_selections;
          }
          
          // CRITICAL: Also create/update a named user_input1 configuration so change types can reference it
          // Check if user_input1 already exists, otherwise create it
          const existingUserInputConfigs = Object.keys(calculationDeps).filter(k => k.match(/^user_input\d+$/));
          const userInput1Key = existingUserInputConfigs.length > 0 
            ? existingUserInputConfigs.sort((a, b) => {
                const numA = parseInt(a.match(/\d+$/)?.[0] || '0', 10);
                const numB = parseInt(b.match(/\d+$/)?.[0] || '0', 10);
                return numA - numB;
              })[0] // Use the first existing user_input config
            : 'user_input1'; // Create new one if none exist
          
          // Create or update the named configuration from Basic Info fields
          calculationDeps[userInput1Key] = {
            label: values.user_input_label || calculationDeps[userInput1Key]?.label || '',
            help_text: values.user_input_help_text || calculationDeps[userInput1Key]?.help_text || '',
            default_value: (values.user_input_default_value !== undefined && values.user_input_default_value !== null && values.user_input_default_value !== '')
              ? values.user_input_default_value
              : (calculationDeps[userInput1Key]?.default_value !== undefined ? calculationDeps[userInput1Key].default_value : undefined),
            allow_multiple_selections: values.allow_multiple_selections !== undefined 
              ? values.allow_multiple_selections 
              : (calculationDeps[userInput1Key]?.allow_multiple_selections !== undefined ? calculationDeps[userInput1Key].allow_multiple_selections : false),
            ...(calculationDeps[userInput1Key] || {}) // Preserve any other fields that might exist
          };
          
          console.log('[usePropertyDetailsForm] Created/updated named user input configuration from Basic Info:', {
            configKey: userInput1Key,
            label: calculationDeps[userInput1Key].label,
            hasHelpText: !!calculationDeps[userInput1Key].help_text,
            hasDefaultValue: calculationDeps[userInput1Key].default_value !== undefined,
            allowMultipleSelections: calculationDeps[userInput1Key].allow_multiple_selections
          });
        }
        
        // Store reference - use global_option_catalog_ref as the reference value
        // This ensures the reference is stored in calculation_dependencies for list-type properties
        if (values.global_option_catalog_ref) {
          calculationDeps.reference = values.global_option_catalog_ref;
        }
        // Also check if there's a separate reference field
        if (values.reference && values.reference !== values.global_option_catalog_ref) {
          calculationDeps.reference = values.reference;
        }
        // Also preserve reference_id if it exists
        if (values.reference_id) {
          calculationDeps.reference_id = values.reference_id;
        }
      }
      
      // For ALL property types with user input: update user_input_label and user_input_help_text if provided
      // This handles updating the user_input1 configuration from Basic Info fields
      // (list-type properties are handled above, but other types like integer, string, etc. also need this)
      if (values.user_input_label !== undefined || values.user_input_help_text !== undefined) {
        const existingUserInputConfigs = Object.keys(calculationDeps).filter(k => k.match(/^user_input\d+$/));
        if (existingUserInputConfigs.length > 0) {
          const userInput1Key = existingUserInputConfigs.sort((a, b) => {
            const numA = parseInt(a.match(/\d+$/)?.[0] || '0', 10);
            const numB = parseInt(b.match(/\d+$/)?.[0] || '0', 10);
            return numA - numB;
          })[0];
          
          // Update the existing user_input1 configuration
          if (!calculationDeps[userInput1Key]) {
            calculationDeps[userInput1Key] = {};
          }
          
          if (values.user_input_label !== undefined) {
            calculationDeps[userInput1Key].label = values.user_input_label;
          }
          if (values.user_input_help_text !== undefined) {
            calculationDeps[userInput1Key].help_text = values.user_input_help_text;
          }
          
          console.log('[usePropertyDetailsForm] Updated user_input configuration from Basic Info fields:', {
            configKey: userInput1Key,
            label: calculationDeps[userInput1Key].label,
            help_text: calculationDeps[userInput1Key].help_text
          });
        }
      }
      
      // For string-type properties: store input mask in calculation_dependencies
      if (values.property_type === 'string') {
        if (values.user_input_mask) {
          calculationDeps.user_input_mask = values.user_input_mask;
          if (values.user_input_mask === 'custom' && values.user_input_mask_pattern) {
            calculationDeps.user_input_mask_pattern = values.user_input_mask_pattern;
          } else if (values.user_input_mask !== 'custom') {
            // Clear custom pattern if not using custom mask
            delete calculationDeps.user_input_mask_pattern;
          }
          
          // Also store in user_input configuration if it exists
          const existingUserInputConfigs = Object.keys(calculationDeps).filter(k => k.match(/^user_input\d+$/));
          if (existingUserInputConfigs.length > 0) {
            const userInput1Key = existingUserInputConfigs.sort((a, b) => {
              const numA = parseInt(a.match(/\d+$/)?.[0] || '0', 10);
              const numB = parseInt(b.match(/\d+$/)?.[0] || '0', 10);
              return numA - numB;
            })[0];
            
            if (calculationDeps[userInput1Key]) {
              calculationDeps[userInput1Key].input_mask = values.user_input_mask;
              if (values.user_input_mask === 'custom' && values.user_input_mask_pattern) {
                calculationDeps[userInput1Key].input_mask_pattern = values.user_input_mask_pattern;
              } else if (values.user_input_mask !== 'custom') {
                delete calculationDeps[userInput1Key].input_mask_pattern;
              }
            }
          }
        }
      }
      
      // For calculation-type properties: store calculation type and source properties
      if (values.calculation_type && values.source_properties?.length > 0) {
        const sourceProps = values.source_properties.reduce((acc: Record<string, any>, prop: any) => {
          acc[prop.operator] = {
            property_key: prop.property_key,
            source: prop.source,
            description: prop.description
          };
          return acc;
        }, {});
        calculationDeps.type = values.calculation_type;
        calculationDeps.source_properties = sourceProps;
      }
      
      // Include referenced_properties in calculation_dependencies (for References tab)
      // This should ALWAYS be saved to calculation_dependencies if present
      if (values.referenced_properties !== undefined) {
        if (Array.isArray(values.referenced_properties) && values.referenced_properties.length > 0) {
          // Filter out any empty/invalid entries
          const validReferences = values.referenced_properties.filter((ref: any) => 
            ref && (ref.property_key || ref.artifact_type)
          );
          if (validReferences.length > 0) {
            calculationDeps.referenced_properties = validReferences;
            console.log('[usePropertyDetailsForm] Saving referenced_properties to calculation_dependencies:', {
              count: validReferences.length,
              references: validReferences
            });
          } else {
            // Empty array means clear references
            calculationDeps.referenced_properties = [];
            console.log('[usePropertyDetailsForm] Setting referenced_properties to empty array in calculation_dependencies');
          }
        } else {
          // null or empty array - explicitly clear
          calculationDeps.referenced_properties = [];
          console.log('[usePropertyDetailsForm] Clearing referenced_properties in calculation_dependencies');
        }
      }
      
      // calculationDeps already contains merged state + form values above
      
      // CRITICAL: Preserve boolean_conditions for Boolean properties
      // boolean_conditions are stored in calculation_dependencies and should be preserved when saving
      if (values.property_type === 'boolean' && values.calculation_dependencies?.boolean_conditions) {
        calculationDeps.boolean_conditions = values.calculation_dependencies.boolean_conditions;
        console.log('[usePropertyDetailsForm] Preserving boolean_conditions in calculation_dependencies:', {
          hasTrueCondition: !!calculationDeps.boolean_conditions.true_condition,
          hasFalseCondition: !!calculationDeps.boolean_conditions.false_condition
        });
      }
      
      // CRITICAL: Ensure all configurations referenced in transition_changes are included in calculation_dependencies
      // This is necessary because when only updating certain fields (like user_input), the referenced configs
      // (like reference1, calculation1, etc.) must still be present in calculation_dependencies
      if (localChangeTypeDefinitions && Array.isArray(localChangeTypeDefinitions) && localChangeTypeDefinitions.length > 0) {
        const existingCalcDeps = calculationDependencies && typeof calculationDependencies === 'object' ? calculationDependencies : {};
        
        localChangeTypeDefinitions.forEach((changeType: any) => {
          // Ensure all referenced configurations from transition_changes are included
          if (changeType.user_input && typeof changeType.user_input === 'string') {
            if (existingCalcDeps[changeType.user_input] && !calculationDeps[changeType.user_input]) {
              calculationDeps[changeType.user_input] = existingCalcDeps[changeType.user_input];
              console.log(`[usePropertyDetailsForm] Including referenced user_input config: ${changeType.user_input}`);
            }
          }
          if (changeType.calculation && typeof changeType.calculation === 'string') {
            if (existingCalcDeps[changeType.calculation] && !calculationDeps[changeType.calculation]) {
              calculationDeps[changeType.calculation] = existingCalcDeps[changeType.calculation];
              console.log(`[usePropertyDetailsForm] Including referenced calculation config: ${changeType.calculation}`);
            }
          }
          if (changeType.reference && typeof changeType.reference === 'string') {
            if (existingCalcDeps[changeType.reference] && !calculationDeps[changeType.reference]) {
              calculationDeps[changeType.reference] = existingCalcDeps[changeType.reference];
              console.log(`[usePropertyDetailsForm] Including referenced reference config: ${changeType.reference}`);
            }
          }
        });
      }
      
      // CRITICAL: Clean up old array-based calculation_dependencies (keys like "0", "1", etc.)
      // These are remnants from the old format and should be removed
      Object.keys(calculationDeps).forEach(key => {
        if (key.match(/^\d+$/)) {
          delete calculationDeps[key];
          console.log(`[usePropertyDetailsForm] Removed old array-based calculation_dependencies key: ${key}`);
        }
      });
      
      // Always set calculation_dependencies if it has at least one field (including referenced_properties)
      // CRITICAL: Also include if transition_changes is being saved, because backend validation requires
      // all referenced configurations (user_input1, reference1, calculation1, etc.) to exist in calculation_dependencies
      const shouldIncludeCalcDeps = (saveAll || fieldsToSaveToUse?.includes('calculation_dependencies') || 
        (fieldsToSaveToUse?.includes('transition_changes') && 
         localChangeTypeDefinitions && 
         Array.isArray(localChangeTypeDefinitions) && 
         localChangeTypeDefinitions.length > 0)) && 
        Object.keys(calculationDeps).length > 0;
      
      if (shouldIncludeCalcDeps) {
        rawPayload.calculation_dependencies = calculationDeps;
        console.log('[usePropertyDetailsForm] Final calculation_dependencies being saved:', {
          keys: Object.keys(calculationDeps),
          hasReferencedProperties: !!calculationDeps.referenced_properties,
          referencedPropertiesCount: calculationDeps.referenced_properties?.length || 0,
          preservedFromExisting: calculationDependencies ? Object.keys(calculationDependencies).length : 0,
          reason: saveAll ? 'saveAll' : fieldsToSaveToUse?.includes('calculation_dependencies') ? 'explicit' : 'transition_changes_dependency'
        });
      }
      
      // Legacy state availability fields are no longer used - removed
      
      // Add basic fields (only if they have values, normalize empty strings to null)
      if (values.property_type !== undefined) rawPayload.property_type = values.property_type;
      if (values.description !== undefined) {
        const normalizedDesc = normalizeValue(values.description);
        if (normalizedDesc !== undefined) rawPayload.description = normalizedDesc;
      }
      if (values.default_value !== undefined && values.default_value !== null && values.default_value !== '') {
        if (typeof values.default_value === 'object' && !Array.isArray(values.default_value)) {
          rawPayload.default_value = values.default_value;
        }
      }
      if (values.input_source) rawPayload.input_source = values.input_source;
      if (values.global_option_catalog_ref !== undefined) {
        const normalizedRef = normalizeValue(values.global_option_catalog_ref);
        if (normalizedRef !== undefined) rawPayload.global_option_catalog_ref = normalizedRef;
      }
      
      // Include state_actions if present - send as array format
      // CRITICAL: The change_type field in state_actions MUST reference a change_type_name that exists in transition_changes
      // Only validate and process state_actions if:
      // 1. User has explicitly modified state actions (stateActionsModified === true), OR
      // 2. We're explicitly clearing it (localStateActions === undefined)
      // Otherwise, allow saving calculation_dependencies and validation_rules independently
      // Also check if we're saving state_actions (either saveAll or explicitly selected)
      if ((saveAll || fieldsToSaveToUse?.includes('state_actions')) && 
          stateActionsModified && localStateActions !== undefined && localStateActions !== null) {
        console.log('[usePropertyDetailsForm] Processing state_actions - BEFORE conversion:', {
          localStateActions,
          localStateActionsType: typeof localStateActions,
          isArray: Array.isArray(localStateActions),
          stateActionsModified,
          localChangeTypeDefinitionsCount: localChangeTypeDefinitions?.length || 0,
          localChangeTypeDefinitionsNames: localChangeTypeDefinitions?.map(ct => ct.change_type_name) || []
        });
        
        const stateActionsArray = convertStateActionsToArray(localStateActions);
        console.log('[usePropertyDetailsForm] Processing state_actions - AFTER conversion to array:', {
          stateActionsArrayLength: stateActionsArray.length,
          stateActionsArray: stateActionsArray.map(e => ({
            state: e.state,
            flow_type: e.flow_type,
            state_action_type: e.state_action_type,
            change_type: e.change_type
          }))
        });
        
        // Check if we actually have any state actions to process
        // Also check if the nested format is actually empty
        const hasStateActions = stateActionsArray.length > 0 || 
          (typeof localStateActions === 'object' && 
           !Array.isArray(localStateActions) &&
           ((localStateActions.operational && Object.keys(localStateActions.operational).length > 0) ||
            (localStateActions.improvement && Object.keys(localStateActions.improvement).length > 0)));
        
        if (hasStateActions) {
          // Get all available change type names from transition_changes
          const transitionChangeNames = new Set(localChangeTypeDefinitions?.map(ct => ct.change_type_name) || []);
          
          console.log('[usePropertyDetailsForm] Available change type names for mapping:', {
            transitionChangeNames: Array.from(transitionChangeNames),
            stateActionsToMap: stateActionsArray.map(e => ({
              state: e.state,
              flow_type: e.flow_type,
              state_action_type: e.state_action_type,
              current_change_type: e.change_type
            }))
          });
          
          // DEFENSIVE: Pre-filter entries that can't be mapped before processing
          // This prevents validation errors when backend has stale data
          const mappableEntries: typeof stateActionsArray = [];
          const unmappableEntries: typeof stateActionsArray = [];
          
          stateActionsArray.forEach(entry => {
            const matchingChangeType = localChangeTypeDefinitions?.find(ct => 
              ct.state_action_type === entry.state_action_type
            );
            if (matchingChangeType || transitionChangeNames.has(entry.change_type || '')) {
              mappableEntries.push(entry);
            } else {
              unmappableEntries.push(entry);
            }
          });
          
          if (unmappableEntries.length > 0) {
            console.warn('[usePropertyDetailsForm] Found state actions that cannot be mapped to any change types. These will be filtered out:', {
              unmappableEntries: unmappableEntries.map(e => ({
                state: e.state,
                flow_type: e.flow_type,
                state_action_type: e.state_action_type,
                change_type: e.change_type
              })),
              availableChangeTypes: Array.from(transitionChangeNames),
              action: 'Will filter out unmappable entries and only save mappable ones, or set state_actions to null if none are mappable'
            });
            
            // If ALL entries are unmappable, clear state_actions entirely
            if (mappableEntries.length === 0) {
              console.log('[usePropertyDetailsForm] All state actions are unmappable - clearing state_actions on backend');
              rawPayload.state_actions = null;
              // Skip the rest of the processing
            } else {
              // Process only mappable entries
              stateActionsArray.length = 0;
              stateActionsArray.push(...mappableEntries);
            }
          }
          
          // Only proceed with mapping if we have mappable entries and haven't already set state_actions to null
          if (rawPayload.state_actions !== null && stateActionsArray.length > 0) {
            // Map state_action_type to change_type_name from transition_changes
            // Find the matching change type for each state action entry
            const cleanedStateActionsArray = stateActionsArray.map(entry => {
            // Find a change type in transition_changes that matches this state_action_type
            const matchingChangeType = localChangeTypeDefinitions?.find(ct => {
              // Match by state_action_type
              return ct.state_action_type === entry.state_action_type;
            });
            
            // If we found a matching change type, use its change_type_name
            // Otherwise, try to match by the original change_type from the entry
            let changeTypeName = matchingChangeType?.change_type_name;
            
            if (!changeTypeName && entry.change_type) {
              // Fallback: check if change_type matches any change_type_name
              const exactMatch = localChangeTypeDefinitions?.find(ct => 
                ct.change_type_name === entry.change_type
              );
              if (exactMatch) {
                changeTypeName = exactMatch.change_type_name;
              }
            }
            
            // If still no match, try to infer from state_action_type (last resort)
            if (!changeTypeName && entry.state_action_type && entry.state_action_type !== 'none') {
              // Try to find any change type with matching state_action_type
              // This handles the case where the change type name doesn't exactly match
              const inferredMatch = localChangeTypeDefinitions?.find(ct => 
                ct.state_action_type === entry.state_action_type
              );
              if (inferredMatch) {
                changeTypeName = inferredMatch.change_type_name;
              }
            }
            
            // CRITICAL: If no matching change type is found, this is an error condition
            // Don't fall back to entry.change_type as it might be invalid
            if (!changeTypeName || !transitionChangeNames.has(changeTypeName)) {
              const errorDetails = {
                state: entry.state,
                flow_type: entry.flow_type,
                state_action_type: entry.state_action_type,
                attempted_change_type: entry.change_type,
                available_change_types: Array.from(transitionChangeNames),
                available_state_action_types: localChangeTypeDefinitions?.map(ct => ct.state_action_type) || []
              };
              console.error('[usePropertyDetailsForm] Cannot map state action to change type:', errorDetails);
              
              // Throw a descriptive error
              throw new Error(
                `Cannot find a matching change type for state action: ${entry.flow_type}.${entry.state} (state_action_type: ${entry.state_action_type}). ` +
                `Available change types: ${Array.from(transitionChangeNames).join(', ') || 'none'}. ` +
                `Please create a change type with state_action_type '${entry.state_action_type}' in the Change Types tab, or update the state action.`
              );
            }
            
            // Return the entry with change_type set to the matched change_type_name
            return {
              ...entry,
              change_type: changeTypeName
            };
          });
          
          // Final validation: ensure all change_type references exist (redundant but safe)
          const missingChangeTypes = cleanedStateActionsArray
            .filter(entry => entry.change_type && !transitionChangeNames.has(entry.change_type))
            .map(entry => `${entry.flow_type}.${entry.state}: change_type '${entry.change_type}'`);
          
          if (missingChangeTypes.length > 0) {
            const errorMessage = `The following state actions reference change types that don't exist in transition_changes:\n${missingChangeTypes.join('\n')}\n\nPlease ensure all referenced change types exist in the Change Types tab.`;
            console.error('[usePropertyDetailsForm] State action validation failed:', missingChangeTypes);
            throw new Error(errorMessage);
          }
          
          rawPayload.state_actions = cleanedStateActionsArray;
          
          console.log('[usePropertyDetailsForm] Mapped state_actions change_types:', {
            entries: cleanedStateActionsArray.map(e => ({
              state: e.state,
              flow_type: e.flow_type,
              state_action_type: e.state_action_type,
              change_type: e.change_type
            })),
            availableChangeTypeNames: Array.from(transitionChangeNames)
          });
          } else {
            // Already set to null in pre-filtering (all entries were unmappable)
            console.log('[usePropertyDetailsForm] Skipping state_actions processing - already set to null due to unmappable entries');
          }
        } else {
          // No state actions to process - explicitly set to null to clear any existing state_actions
          console.log('[usePropertyDetailsForm] No state actions to save, setting state_actions to null');
          rawPayload.state_actions = null;
        }
      } else if ((saveAll || fieldsToSaveToUse?.includes('state_actions')) && 
                 stateActionsModified && (localStateActions === undefined || localStateActions === null)) {
        // User has modified state actions and explicitly cleared them - set to null to clear on backend
        console.log('[usePropertyDetailsForm] State actions modified and cleared - setting state_actions to null');
        rawPayload.state_actions = null;
      } else if (!stateActionsModified && localStateActions === undefined) {
        // User hasn't modified state actions and they're undefined - don't include in payload
        // This allows saving calculation_dependencies and validation_rules independently
        // Only skip if we're not explicitly saving state_actions
        if (!saveAll && !fieldsToSaveToUse?.includes('state_actions')) {
          console.log('[usePropertyDetailsForm] State actions not modified and undefined - skipping state_actions in payload');
        } else {
          console.log('[usePropertyDetailsForm] Saving state_actions as null (explicitly selected)');
          rawPayload.state_actions = null;
        }
      } else {
        // Fallback: if state actions exist but weren't modified, don't include in payload
        if (!saveAll && !fieldsToSaveToUse?.includes('state_actions')) {
          console.log('[usePropertyDetailsForm] State actions exist but not modified - skipping state_actions in payload');
        }
      }
      
      // Save transition_changes directly (already in reference-based format)
      console.log('[usePropertyDetailsForm] Preparing save - localChangeTypeDefinitions (TransitionChangeReference[]):', {
        value: localChangeTypeDefinitions,
        isUndefined: localChangeTypeDefinitions === undefined,
        isArray: Array.isArray(localChangeTypeDefinitions),
        count: localChangeTypeDefinitions?.length || 0,
        names: localChangeTypeDefinitions?.map(ct => ct.change_type_name) || []
      });
      
      // Use TransitionChangeReference[] directly as transition_changes
      // Only include if saving all fields or explicitly selected
      if ((saveAll || fieldsToSaveToUse?.includes('transition_changes')) &&
          localChangeTypeDefinitions !== undefined && Array.isArray(localChangeTypeDefinitions) && localChangeTypeDefinitions.length > 0) {
        rawPayload.transition_changes = localChangeTypeDefinitions;
        
        console.log('[usePropertyDetailsForm] Saving transition_changes (reference-based format):', {
          count: localChangeTypeDefinitions.length,
          transitionChanges: localChangeTypeDefinitions
        });
      } else if ((saveAll || fieldsToSaveToUse?.includes('transition_changes')) &&
                 localChangeTypeDefinitions !== undefined && Array.isArray(localChangeTypeDefinitions) && localChangeTypeDefinitions.length === 0) {
        // Explicitly set to empty array if user cleared all change types
        rawPayload.transition_changes = [];
      }
      
      // Note: calculation_dependencies and validation_rules should already contain named configurations
      // They are edited in their respective tabs and don't need to be extracted here
      
      if (!Array.isArray(rawPayload.transition_changes)) {
        console.warn('[usePropertyDetailsForm] transition_changes is not an array, setting to empty array');
        rawPayload.transition_changes = [];
      }
      
      // Filter to only include allowed fields
      const updatePayload: any = {};
      for (const key of allowedFields) {
        if (key in rawPayload && rawPayload[key] !== undefined) {
          updatePayload[key] = rawPayload[key];
        }
      }
      
      if ('property_key' in updatePayload) {
        delete updatePayload.property_key;
      }

      // Validate and clean reference-based transition changes
      if (Array.isArray(updatePayload.transition_changes)) {
        // Merge existing calculation_dependencies and validation_rules with payload data
        // CRITICAL: Use updatePayload.calculation_dependencies which includes:
        // 1. Existing configurations from state (calculationDependencies)
        // 2. Newly created configurations from tabs (form values)
        // 3. Basic Info user input fields converted to user_input1
        // This ensures we check against all configurations that will be saved
        const mergedCalculationDependencies = {
          ...(calculationDependencies || {}),
          ...(updatePayload.calculation_dependencies || {}) // This includes user_input1 created from Basic Info fields
        };
        // For validation, use the payload validation_rules (which already contains merged state + form values)
        const mergedValidationRules = updatePayload.validation_rules || validationRules || {};
        
        // Get available named configurations for validation (using merged data)
        const availableConfigs = getAvailableNamedConfigurations(
          mergedCalculationDependencies,
          mergedValidationRules
        );
        
        console.log('[usePropertyDetailsForm] Validating transition_changes references:', {
          existingCalcDepsKeys: calculationDependencies ? Object.keys(calculationDependencies) : [],
          payloadCalcDepsKeys: updatePayload.calculation_dependencies ? Object.keys(updatePayload.calculation_dependencies) : [],
          mergedCalcDepsKeys: Object.keys(mergedCalculationDependencies),
          availableConfigs: availableConfigs
        });
        
        const validationErrors: string[] = [];
        
        const validTransitionChanges = updatePayload.transition_changes.map((tc: any, index: number) => {
          // Remove any legacy change_type field (should use change_type_name instead)
          if (tc && typeof tc === 'object') {
            const { change_type, ...cleanedTc } = tc;
            if (change_type !== undefined) {
              console.warn('[usePropertyDetailsForm] Removing legacy change_type field from transition_change:', change_type);
            }
            
            // Validate required references based on state_action_type
            if (cleanedTc.state_action_type === 'User' && !cleanedTc.user_input) {
              validationErrors.push(`transition_changes[${index}] with state_action_type 'User' must have 'user_input' reference (string)`);
            }
            if (cleanedTc.state_action_type === 'Calculate' && !cleanedTc.calculation) {
              validationErrors.push(`transition_changes[${index}] with state_action_type 'Calculate' must have 'calculation' reference (string)`);
            }
            if (cleanedTc.state_action_type === 'System' && !cleanedTc.calculation) {
              validationErrors.push(`transition_changes[${index}] with state_action_type 'System' must have 'calculation' reference (string)`);
            }
            if (cleanedTc.state_action_type === 'Reference' && !cleanedTc.reference) {
              validationErrors.push(`transition_changes[${index}] with state_action_type 'Reference' must have 'reference' reference (string)`);
            }
            
            // Validate that referenced configurations exist
            if (cleanedTc.user_input && !availableConfigs.user_inputs.includes(cleanedTc.user_input)) {
              validationErrors.push(`transition_changes[${index}].user_input references '${cleanedTc.user_input}' which does not exist in calculation_dependencies`);
            }
            if (cleanedTc.calculation && !availableConfigs.calculations.includes(cleanedTc.calculation)) {
              validationErrors.push(`transition_changes[${index}].calculation references '${cleanedTc.calculation}' which does not exist in calculation_dependencies`);
            }
            if (cleanedTc.reference && !availableConfigs.references.includes(cleanedTc.reference)) {
              validationErrors.push(`transition_changes[${index}].reference references '${cleanedTc.reference}' which does not exist in calculation_dependencies`);
            }
            if (cleanedTc.condition && !availableConfigs.conditions.includes(cleanedTc.condition)) {
              validationErrors.push(`transition_changes[${index}].condition references '${cleanedTc.condition}' which does not exist in validation_rules`);
            }
            if (cleanedTc.validation && !availableConfigs.validations.includes(cleanedTc.validation)) {
              validationErrors.push(`transition_changes[${index}].validation references '${cleanedTc.validation}' which does not exist in validation_rules`);
            }
            if (cleanedTc.resource && !availableConfigs.resources.includes(cleanedTc.resource)) {
              validationErrors.push(`transition_changes[${index}].resource references '${cleanedTc.resource}' which does not exist in validation_rules`);
            }
            
            return cleanedTc;
          }
          return tc;
        }).filter((tc: any) => {
          const isValid = tc && 
                         typeof tc === 'object' && 
                         typeof tc.change_type_name === 'string' && 
                         tc.change_type_name.length > 0 &&
                         typeof tc.state_action_type === 'string' && 
                         tc.state_action_type.length > 0;
          
          if (!isValid) {
            console.warn('[usePropertyDetailsForm] Filtering out invalid transition_change entry (missing change_type_name or state_action_type):', tc);
          }
          
          return isValid;
        });
        
        // If there are validation errors, throw an error before sending to backend
        if (validationErrors.length > 0) {
          const errorMessage = `Validation error: Invalid transition_changes structure:\n${validationErrors.join('\n')}\n\nPlease ensure all referenced configurations exist in calculation_dependencies or validation_rules before saving.`;
          console.error('[usePropertyDetailsForm] Transition changes validation failed:', validationErrors);
          throw new Error(errorMessage);
        }
        
        updatePayload.transition_changes = validTransitionChanges;
        
        console.log('[usePropertyDetailsForm] Final transition_changes validation (reference-based format):', {
          validCount: updatePayload.transition_changes.length,
          allValid: updatePayload.transition_changes.every((tc: any) => 
            typeof tc.change_type_name === 'string' && tc.change_type_name.length > 0 &&
            typeof tc.state_action_type === 'string' && tc.state_action_type.length > 0
          )
        });
      }

      // Clean transition_changes to remove any change_type fields (should use change_type_name instead)
      // NOTE: state_actions entries SHOULD have change_type field (required by backend)
      if (Array.isArray(updatePayload.transition_changes)) {
        updatePayload.transition_changes = updatePayload.transition_changes.map((entry: any) => {
          if (entry && typeof entry === 'object' && 'change_type' in entry) {
            const { change_type, ...cleanedEntry } = entry;
            if (change_type !== undefined) {
              console.warn('[usePropertyDetailsForm] Removing legacy change_type field from transition_change:', change_type);
            }
            return cleanedEntry;
          }
          return entry;
        });
      }

      // Deep clean: Remove change_type fields ONLY from transition_changes (not from state_actions)
      // state_actions entries MUST have change_type field (required by backend validation)
      const removeChangeTypeFromTransitionChanges = (obj: any, isTransitionChanges: boolean = false): any => {
        if (obj === null || obj === undefined) return obj;
        if (Array.isArray(obj)) {
          // If this is the transition_changes array, remove change_type from its entries
          if (isTransitionChanges) {
            return obj.map(item => {
              if (item && typeof item === 'object' && 'change_type' in item) {
                const { change_type, ...cleaned } = item;
                console.warn('[usePropertyDetailsForm] Removing change_type field from transition_change:', change_type);
                return cleaned;
              }
              return item;
            });
          }
          return obj.map(item => removeChangeTypeFromTransitionChanges(item, false));
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          Object.keys(obj).forEach(key => {
            // Only remove change_type from transition_changes, NOT from state_actions
            if (key === 'change_type' && isTransitionChanges) {
              console.warn('[usePropertyDetailsForm] Removing change_type field from transition_change at:', key);
              return; // Skip change_type fields in transition_changes
            }
            // Recursively process, marking if we're in transition_changes context
            cleaned[key] = removeChangeTypeFromTransitionChanges(
              obj[key], 
              key === 'transition_changes' || isTransitionChanges
            );
          });
          return cleaned;
        }
        return obj;
      };
      
      // Apply deep cleaning to remove change_type only from transition_changes
      updatePayload.transition_changes = removeChangeTypeFromTransitionChanges(
        updatePayload.transition_changes, 
        true
      );

      // Merge existing calculation_dependencies and validation_rules into updatePayload
      // This ensures named configurations (user_input1, calculation1, etc.) that weren't modified
      // are still included in the payload sent to the backend
      if (calculationDependencies && typeof calculationDependencies === 'object') {
        updatePayload.calculation_dependencies = {
          ...calculationDependencies,
          ...(updatePayload.calculation_dependencies || {})
        };
      }
      // Merge existing validation_rules state with form values (prioritize form values)
      if (validationRules && typeof validationRules === 'object') {
        updatePayload.validation_rules = {
          ...validationRules, // Existing state (base)
          ...(updatePayload.validation_rules || {}) // Form values override (prioritized)
        };
        console.log('[usePropertyDetailsForm] Merged validation_rules (state + form):', {
          keys: Object.keys(updatePayload.validation_rules || {}),
          resourceKeys: Object.keys(updatePayload.validation_rules || {}).filter(k => k.match(/^resource\d+$/))
        });
      } else if (updatePayload.validation_rules) {
        console.log('[usePropertyDetailsForm] Using validation_rules from form only:', {
          keys: Object.keys(updatePayload.validation_rules),
          resourceKeys: Object.keys(updatePayload.validation_rules).filter(k => k.match(/^resource\d+$/))
        });
      }
      
      // NOTE: We intentionally keep change_type in state_actions (required by backend)
      // Only remove change_type from transition_changes (which use change_type_name instead)
      // The cleaning for transition_changes was already applied above
      const cleanedPayload = updatePayload;

      // Deep inspection: Check for change_type values (should only be in state_actions, not in transition_changes)
      const inspectForChangeType = (obj: any, path: string = ''): Array<{path: string, value: any, context: string}> => {
        const found: Array<{path: string, value: any, context: string}> = [];
        if (obj === null || obj === undefined) return found;
        if (Array.isArray(obj)) {
          const currentPath = path || '';
          // Check if this is state_actions or transition_changes array
          const isStateActions = currentPath.includes('state_actions');
          const isTransitionChanges = currentPath.includes('transition_changes');
          
          obj.forEach((item, idx) => {
            const itemPath = `${currentPath}[${idx}]`;
            if (typeof item === 'object' && item !== null && 'change_type' in item) {
              if (isTransitionChanges) {
                found.push({ 
                  path: itemPath, 
                  value: item.change_type, 
                  context: 'transition_changes (should be removed)' 
                });
              } else if (isStateActions) {
                found.push({ 
                  path: itemPath, 
                  value: item.change_type, 
                  context: 'state_actions (required by backend)' 
                });
              }
            }
            found.push(...inspectForChangeType(item, itemPath));
          });
        } else if (typeof obj === 'object') {
          Object.keys(obj).forEach(key => {
            found.push(...inspectForChangeType(obj[key], path ? `${path}.${key}` : key));
          });
        }
        return found;
      };

      const changeTypeValues = inspectForChangeType(cleanedPayload);
      const transitionChangesWithChangeType = changeTypeValues.filter(v => v.context.includes('transition_changes'));
      if (transitionChangesWithChangeType.length > 0) {
        console.warn('[usePropertyDetailsForm] Found change_type values in transition_changes (should have been removed):', transitionChangesWithChangeType);
      } else {
        const stateActionsWithChangeType = changeTypeValues.filter(v => v.context.includes('state_actions'));
        console.log('[usePropertyDetailsForm] Payload cleaned successfully:', {
          transition_changes_change_type_removed: true,
          state_actions_change_type_present: stateActionsWithChangeType.length > 0,
          state_actions_count: stateActionsWithChangeType.length
        });
      }

      try {
        // Enhanced logging for state_actions to debug "System" state action issue
        const stateActionsLog: any = {
          isNull: cleanedPayload.state_actions === null,
          isUndefined: cleanedPayload.state_actions === undefined,
          isArray: Array.isArray(cleanedPayload.state_actions),
          count: Array.isArray(cleanedPayload.state_actions) ? cleanedPayload.state_actions.length : 'not array',
          entries: Array.isArray(cleanedPayload.state_actions) ? cleanedPayload.state_actions.map((e: any) => ({
            state: e.state,
            flow_type: e.flow_type,
            state_action_type: e.state_action_type,
            change_type: e.change_type, // This should be removed by cleaning, but log it if present
            hasChangeType: 'change_type' in (e || {})
          })) : 'not array'
        };
        
        console.log('[usePropertyDetailsForm] ========== FINAL PAYLOAD BEFORE API CALL ==========');
        console.log('[usePropertyDetailsForm] Property Key:', selectedPropertyKey);
        console.log('[usePropertyDetailsForm] Payload Keys:', Object.keys(cleanedPayload));
        console.log('[usePropertyDetailsForm] State Actions:', stateActionsLog);
        console.log('[usePropertyDetailsForm] Transition Changes:', {
          count: Array.isArray(cleanedPayload.transition_changes) ? cleanedPayload.transition_changes.length : 'not array',
          change_type_names: Array.isArray(cleanedPayload.transition_changes) 
            ? cleanedPayload.transition_changes.map((tc: any) => tc.change_type_name)
            : 'not array',
          sample: Array.isArray(cleanedPayload.transition_changes) && cleanedPayload.transition_changes.length > 0 
            ? cleanedPayload.transition_changes[0] 
            : 'none'
        });
        console.log('[usePropertyDetailsForm] Calculation Dependencies Keys:', cleanedPayload.calculation_dependencies ? Object.keys(cleanedPayload.calculation_dependencies) : 'none');
        console.log('[usePropertyDetailsForm] Validation Rules Keys:', cleanedPayload.validation_rules ? Object.keys(cleanedPayload.validation_rules) : 'none');
        console.log('[usePropertyDetailsForm] ===================================================');
        
        // Log the exact payload being sent
        console.log('[usePropertyDetailsForm] ========== REQUEST PAYLOAD TO BACKEND ==========');
        console.log('[usePropertyDetailsForm] Full Payload (stringified):', JSON.stringify(cleanedPayload, null, 2));
        console.log('[usePropertyDetailsForm] State Actions in Request:', {
          present: 'state_actions' in cleanedPayload,
          value: cleanedPayload.state_actions,
          isNull: cleanedPayload.state_actions === null,
          isUndefined: cleanedPayload.state_actions === undefined,
          stringified: JSON.stringify(cleanedPayload.state_actions, null, 2)
        });
        console.log('[usePropertyDetailsForm] ================================================');
        
        const response = await apiClient.put(`/properties/${encodeURIComponent(selectedPropertyKey!)}`, cleanedPayload);
        
        console.log('[usePropertyDetailsForm] ========== BACKEND RESPONSE AFTER UPDATE ==========');
        console.log('[usePropertyDetailsForm] Property updated successfully:', {
          property_key: selectedPropertyKey,
          responseStatus: response.status,
          responseData: response.data
        });
        console.log('[usePropertyDetailsForm] State Actions in Response:', {
          raw: response.data?.definition?.state_actions,
          isArray: Array.isArray(response.data?.definition?.state_actions),
          value: response.data?.definition?.state_actions,
          stringified: JSON.stringify(response.data?.definition?.state_actions, null, 2)
        });
        console.log('[usePropertyDetailsForm] Full Response Data:', JSON.stringify(response.data, null, 2));
        console.log('[usePropertyDetailsForm] ===================================================');
        
        void message.success('Property updated successfully');
        
        // Clear stale state if state_actions was set to null in the payload
        // This ensures that after save, local state matches what was sent to backend
        if (cleanedPayload.state_actions === null) {
          setLocalStateActions(undefined);
          setStateActions(undefined);
          console.log('[usePropertyDetailsForm] Cleared local state_actions after saving null to backend');
        }
        
        // Reset modification flag after successful save
        setStateActionsModified(false);
        
        // Reload the property definition to get the updated data
        await loadPropertyDefinitionForDetails();
        
        // Close the form
        onCloseForm?.();
        
        // Notify parent component
        if (onPropertySelect) {
          onPropertySelect(selectedPropertyKey!);
        }
        
        if (onFormSuccess) {
          onFormSuccess();
        }
      } catch (error: any) {
        console.error('[usePropertyDetailsForm] Failed to update property:', error);
        
        // Log the full error response data
        const errorData = error.response?.data;
        console.error('[usePropertyDetailsForm] ========== BACKEND ERROR RESPONSE ==========');
        console.error('[usePropertyDetailsForm] Full error data:', JSON.stringify(errorData, null, 2));
        console.error('[usePropertyDetailsForm] Error message:', errorData?.detail || errorData?.message || errorData?.error || error.message);
        console.error('[usePropertyDetailsForm] Status:', error.response?.status, error.response?.statusText);
        console.error('[usePropertyDetailsForm] Request URL:', error.config?.url);
        console.error('[usePropertyDetailsForm] ============================================');
        
        // Parse validation error if it's a 422
        if (error.response?.status === 422) {
          const errorData = error.response.data;
          console.error('[usePropertyDetailsForm] Validation error details:', errorData);
          
          let errorMessage = 'Validation error';
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              const firstError = errorData.detail[0];
              if (firstError?.msg) {
                errorMessage = firstError.msg;
              } else if (firstError?.loc && firstError?.type) {
                errorMessage = `${firstError.loc.join('.')}: ${firstError.type}`;
              }
            } else if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            }
          }
          
          void message.error(`Failed to update property: ${errorMessage}`);
        } else {
          void message.error(`Failed to update property: ${error.message || 'Unknown error'}`);
        }
      } finally {
        setDetailsLoading(false);
      }
    } catch (error: any) {
      console.error('[usePropertyDetailsForm] Form validation failed:', error);
      // Show the actual error message if it's a validation error
      const errorMessage = error.message || 'Please fix the form errors before submitting';
      void message.error(errorMessage, 10); // Show for 10 seconds since it might be a longer message
      setDetailsLoading(false);
    }
  }, [detailsForm, localChangeTypeDefinitions, localStateActions, localTransitionChanges, selectedPropertyKey, loadPropertyDefinitionForDetails, onPropertySelect, onFormSuccess, onCloseForm, setDetailsLoading, stateActionsModified, calculationDependencies, validationRules]);

  const handleSaveStateActions = useCallback(async () => {
    if (!selectedPropertyKey) {
      void message.error('No property selected');
      return;
    }

    try {
      setDetailsLoading(true);
      
      let cleanedStateActions: StateActions | undefined = localStateActions;
      if (cleanedStateActions) {
        const cleaned: StateActions = {};
        if (cleanedStateActions.operational && Object.keys(cleanedStateActions.operational).length > 0) {
          cleaned.operational = cleanedStateActions.operational;
        }
        if (cleanedStateActions.improvement && Object.keys(cleanedStateActions.improvement).length > 0) {
          cleaned.improvement = cleanedStateActions.improvement;
        }
        cleanedStateActions = Object.keys(cleaned).length > 0 ? cleaned : undefined;
      }
      
      const stateActionsArray = convertStateActionsToArray(cleanedStateActions);
      
      const updatePayload = {
        state_actions: stateActionsArray.length > 0 ? stateActionsArray : (cleanedStateActions === null ? null : undefined)
      };

      const newChangeTypes = autoCreateChangeTypesFromStateActions(cleanedStateActions);
      if (newChangeTypes.length > 0) {
        const existingNames = new Set(localChangeTypeDefinitions.map(ct => ct.change_type_name));
        const uniqueNewTypes = newChangeTypes.filter(ct => {
          const name = ct.change_type_name;
          return name && !existingNames.has(name);
        });
        
        if (uniqueNewTypes.length > 0) {
          setLocalChangeTypeDefinitions([...localChangeTypeDefinitions, ...uniqueNewTypes]);
          void message.info(`Auto-created ${uniqueNewTypes.length} change type(s) from state actions. Switch to "Change Types" tab to configure them.`);
        }
      }
      
      try {
        const _response = await apiClient.put(`/properties/${encodeURIComponent(selectedPropertyKey)}`, updatePayload);
        
        setStateActions(cleanedStateActions);
        setLocalStateActions(cleanedStateActions);
        
        void message.success('State actions saved successfully');
      } catch (putError: any) {
        if (putError.response?.status === 405 || putError.response?.status === 404) {
          const fullDefResponse = await GlobalPropertiesService.getPropertyDefinition(selectedPropertyKey);
          const fullDef = fullDefResponse.definition;
          const importPayload = {
            properties: [{
              ...fullDef,
              state_actions: cleanedStateActions
            }]
          };
          const _importResponse = await apiClient.post('/global-properties/import', importPayload);
          
          setStateActions(cleanedStateActions);
          setLocalStateActions(cleanedStateActions);
          
          void message.success('State actions saved successfully via import');
        } else {
          console.error('Save error:', putError);
          if (putError.response?.status === 422) {
            console.error('Validation error details:', putError.response.data);
          }
          throw putError;
        }
      }
    } catch (error: any) {
      void message.error(`Failed to save state actions: ${error.message || 'Unknown error'}`);
    } finally {
      setDetailsLoading(false);
    }
  }, [selectedPropertyKey, localStateActions, localChangeTypeDefinitions, autoCreateChangeTypesFromStateActions, setLocalChangeTypeDefinitions, setStateActions, setLocalStateActions, setDetailsLoading]);

  // Computed values
  const identifiedTransitions = useMemo(() => {
    if (!stateActions) return [];
    
    const operationalForward = getForwardTransitions('operational');
    const operationalBackward = getBackwardTransitions('operational');
    const improvementForward = getForwardTransitions('improvement');
    const improvementBackward = getBackwardTransitions('improvement');
    const allTransitions = [
      ...operationalForward,
      ...operationalBackward,
      ...improvementForward,
      ...improvementBackward
    ];

    const formatTransitionString = (trans: StandardTransitionInfo): string => {
      return `${trans.fromState} → ${trans.toState}`;
    };
    
    const identified: StandardTransitionInfo[] = [];
    
    if (stateActions.operational) {
      Object.keys(stateActions.operational).forEach((state) => {
        const transitionsToState = allTransitions.filter(t => t.toState === state);
        identified.push(...transitionsToState);
      });
    }
    
    if (stateActions.improvement) {
      Object.keys(stateActions.improvement).forEach((state) => {
        const transitionsToState = allTransitions.filter(t => t.toState === state);
        identified.push(...transitionsToState);
      });
    }
    
    const uniqueIdentified = Array.from(
      new Map(identified.map(t => [formatTransitionString(t), t])).values()
    );
    
    return uniqueIdentified;
  }, [stateActions]);

  const allAvailableTransitions = useMemo(() => {
    const operationalForward = getForwardTransitions('operational');
    const operationalBackward = getBackwardTransitions('operational');
    const improvementForward = getForwardTransitions('improvement');
    const improvementBackward = getBackwardTransitions('improvement');
    return [
      ...operationalForward,
      ...operationalBackward,
      ...improvementForward,
      ...improvementBackward
    ];
  }, []);

  const formatTransitionString = useCallback((trans: StandardTransitionInfo): string => {
    return `${trans.fromState} → ${trans.toState}`;
  }, []);

  const transitionAssignments = useMemo(() => {
    const filteredTransitions = transitionFilter === 'state_action' 
      ? identifiedTransitions 
      : allAvailableTransitions;

    return filteredTransitions.map(trans => {
      const transString = formatTransitionString(trans);
      const existingChange = localTransitionChanges.find(tc => (tc as TransitionChange).transition === transString);
      return {
        transition: transString,
        transitionName: trans.name,
        change_type_ref: (existingChange as TransitionChange)?.change_type_ref || undefined,
        state_action: (existingChange as TransitionChange)?.state_action || undefined
      };
    });
  }, [transitionFilter, identifiedTransitions, allAvailableTransitions, localTransitionChanges, formatTransitionString]);

  const stateActionOptions = useMemo(() => {
    const options: string[] = [];
    if (stateActions?.operational) {
      Object.keys(stateActions.operational).forEach((state) => {
        options.push(`operational.${state}`);
      });
    }
    if (stateActions?.improvement) {
      Object.keys(stateActions.improvement).forEach((state) => {
        options.push(`improvement.${state}`);
      });
    }
    return options;
  }, [stateActions]);

  return {
    // Form state
    detailsForm,
    detailsLoading,
    optionCatalogs,
    detailsPropertyType,
    setDetailsPropertyType,
    
    // State actions
    stateActions,
    localStateActions,
    setStateActions,
    setLocalStateActions,
    
    // Transition changes
    transitionChanges,
    localTransitionChanges,
    setTransitionChanges,
    setLocalTransitionChanges,
    editingTransitionChange,
    setEditingTransitionChange,
    showTransitionChangeEditor,
    setShowTransitionChangeEditor,
    
    // Change types
    changeTypeDefinitions,
    localChangeTypeDefinitions,
    setChangeTypeDefinitions,
    setLocalChangeTypeDefinitions,
    selectedChangeType,
    setSelectedChangeType,
    ensureRequiredConfiguration,
    
    // Dialog state
    stateActionDialog,
    setStateActionDialog,
    
    // Filter state
    transitionFilter,
    setTransitionFilter,
    
    // Functions
    loadPropertyDefinitionForDetails,
    handleDetailsFormSubmit,
    handleSaveStateActions,
    buildStateActionDetails,
    handleDeleteChangeType,
    handleStateClick,
    handleStateActionDialogSave,
    
    // Computed values
    identifiedTransitions,
    allAvailableTransitions,
    formatTransitionString,
    transitionAssignments,
    stateActionOptions,
    improvementPatterns,
    calculationDependencies,
    validationRules
  };
};
